<template>
  <div>
    <v-container fluid>
      <v-skeleton-loader :loading="!flagNav" transition="scale-transition" max-height="64" type="table-heading">
        <v-toolbar dark class="header-title text-white">
          <v-toolbar-title class="title" :style="'color: #' + FontColorTitle">Proactive Message</v-toolbar-title>
          <v-spacer />
          <div class="d-flex pr-4">
            <v-toolbar-title class="title" :style="'color: #' + FontColorTitle">{{ today }}</v-toolbar-title>
          </div>
        </v-toolbar>
      </v-skeleton-loader>
      <v-row v-if="activeProcessingSessionId && currentUserId" class="mb-4 mt-4">
        <v-col cols="12">
          <v-card class="processing-status-card">
            <v-card-text class="py-4">
              <div class="processing-status-header mb-2">
                <div>
                  <div class="processing-status-title">CIF Background Processing</div>
                  <div class="processing-status-subtitle">
                    {{ activeProcessingCompleted ? 'งานล่าสุดเสร็จแล้ว' : 'ระบบกำลังประมวลผล CIF อยู่เบื้องหลัง' }}
                  </div>
                </div>
                <v-chip
                  :color="getCifProcessingStatusColor(activeProcessingCompleted ? 'Completed' : 'Processing')"
                  size="small"
                  variant="tonal"
                >
                  {{ activeProcessingCompleted ? 'Completed' : 'Processing' }}
                </v-chip>
              </div>
              <StatusIndicator
                :user-id="currentUserId"
                :session-id="activeProcessingSessionId"
                :initial-status-list="activeProcessingStatusList"
                :initial-completed="activeProcessingCompleted"
                @status-update="handleBackgroundStatusUpdate"
              />
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
      <v-card class="card2" style="margin-top: 1rem">
        <v-card-title class="py-4 px-4">
          <v-row align="center" no-gutters>
            <!-- Title Section -->
            <v-col cols="12" sm="12" md="3" lg="3" xl="3" class="d-flex align-center pb-3 pb-md-0">
              <v-icon color="blue-grey darken-2 mr-5" size="28">mdi-message-settings</v-icon>
              <div>
                <h3 class="text-h6 mb-1">Proactive Message Configuration</h3>
                <p class="text-caption text-grey-darken-1 mb-0">Configure automated messaging settings and parameters</p>
              </div>
            </v-col>

            <!-- Controls Section -->
            <v-col cols="12" sm="12" md="9" lg="9" xl="9">
              <v-row align="center" justify="end" no-gutters>
                <v-col cols="12" sm="5" md="5" lg="5" xl="5">
                  <v-text-field v-model="searchText" prepend-inner-icon="mdi-magnify" placeholder="Search" hide-details variant="outlined" rounded class="search-field"></v-text-field>
                </v-col>

                <v-col cols="auto" class="ml-2 ml-sm-3">
                  <!-- Export Button -->
                  <v-btn class="btn-export" outlined @click="exportToExcel" :loading="exportLoading">
                    <v-img :src="images.ExcelImg" alt="Excel" width="20" height="20" class="mr-2"></v-img>
                    Export
                  </v-btn>
                </v-col>

                <v-col v-if="permissions.add" cols="auto" class="ml-2 ml-sm-3">
                  <!-- Proactive Button -->
                  <v-btn color="primary" dark class="btn-layout" @click="openAddDialog">
                    <v-icon left>mdi-plus</v-icon>
                    Proactive
                  </v-btn>
                </v-col>

                <v-col cols="auto" class="ml-2">
                  <!-- Menu Button -->
                  <v-btn icon class="btn-filter" small elevation="0" @click="toggleFilter">
                    <v-icon size="20" color="white">mdi-filter-variant</v-icon>
                  </v-btn>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
        </v-card-title>
        <v-card-item>
          <div class="table-container">
            <v-data-table-server :items-per-page="options.itemsPerPage" :items-length="searchTotalTable" v-model:options="options" :items="items" class="my-data-table modern-table" fixed-header>
              <template v-slot:headers>
                <tr>
                  <th style="text-align: center; width: 95px; background-color: #f5f5f5">
                    {{ $t('no') }}
                  </th>
                  <th style="text-align: left; width: 600px; background-color: #f5f5f5">Message</th>
                  <th style="text-align: center; background-color: #f5f5f5">Continue Question</th>
                  <th style="text-align: center; width: 200px; background-color: #f5f5f5">Screen</th>
                  <th style="text-align: center; width: 200px; background-color: #f5f5f5">Target Group</th>
                  <th style="text-align: center; width: 200px; background-color: #f5f5f5">Segment</th>
                  <th style="text-align: center; width: 200px; background-color: #f5f5f5">Wealth Membership</th>
                  <th style="text-align: center; background-color: #f5f5f5">Priority</th>
                  <th style="text-align: center; width: 400px; background-color: #f5f5f5">Effective Start Date &amp; Time</th>
                  <th style="text-align: center; width: 400px; background-color: #f5f5f5">Effective End Date &amp; Time</th>
                  <th style="text-align: center; width: 300px; background-color: #f5f5f5">Create Date</th>
                  <th class="sticky-status" style="text-align: center; width: 230px; background-color: #f5f5f5">Status</th>
                  <th class="sticky-repeat" style="text-align: center; width: 230px; background-color: #f5f5f5">Repeat Flag</th>
                  <th class="sticky-action" style="text-align: center; width: 230px; background-color: #f5f5f5">Action</th>
                </tr>
              </template>
              <template v-slot:item="{ item }">
                <tr>
                  <td style="text-align: center">
                    <div class="d-flex flex-column align-center gap-1">
                      <span>{{ item.number }}</span>
                      <v-chip v-if="item.defaultFlag" color="primary" size="x-small" variant="tonal">
                        <v-icon size="10" class="mr-1">mdi-star</v-icon>
                        Default
                      </v-chip>
                      <!-- Risk 1: this message's Pending-in-State condition names a value that is not
                           a group state (e.g. a pre-group RESUME_* state code), so no form's
                           CurrentStateGroup can ever equal it and the message can never be shown.
                           Nothing else on the row looks wrong, which is the entire reason for the badge. -->
                      <v-tooltip v-if="item.removedTaxConditionStates.length" location="top">
                        <template v-slot:activator="{ props }">
                          <v-chip v-bind="props" color="warning" size="x-small" variant="tonal" class="removed-state-chip">
                            <v-icon size="10" class="mr-1">mdi-alert-outline</v-icon>
                            State removed
                          </v-chip>
                        </template>
                        <span>
                          This message will never be shown: its Tax Planning condition uses
                          <strong>{{ item.removedTaxConditionStates.join(', ') }}</strong>,
                          which {{ item.removedTaxConditionStates.length === 1 ? 'is not a state group' : 'are not state groups' }}.
                          Edit it to pick a state group.
                        </span>
                      </v-tooltip>
                    </div>
                  </td>
                  <td>
                    <div class="message-multilang">
                      <div v-if="item.messageThai" class="message-box message-box-thai">
                        <div class="message-lang-header">
                          <span class="lang-label">TH</span>
                        </div>
                        <span class="message-text">
                          <v-tooltip activator="parent" location="top" max-width="400">{{ item.messageThai }}</v-tooltip>
                          {{ item.messageThai }}
                        </span>
                      </div>
                      <div v-if="item.messageEnglish" class="message-box message-box-english">
                        <div class="message-lang-header">
                          <span class="lang-label">EN</span>
                        </div>
                        <span class="message-text">
                          <v-tooltip activator="parent" location="top" max-width="400">{{ item.messageEnglish }}</v-tooltip>
                          {{ item.messageEnglish }}
                        </span>
                      </div>
                      <div v-if="item.messageChinese" class="message-box message-box-chinese">
                        <div class="message-lang-header">
                          <span class="lang-label">CN</span>
                        </div>
                        <span class="message-text">
                          <v-tooltip activator="parent" location="top" max-width="400">{{ item.messageChinese }}</v-tooltip>
                          {{ item.messageChinese }}
                        </span>
                      </div>
                      <span v-if="!item.messageThai && !item.messageEnglish && !item.messageChinese" class="text-grey">No message</span>
                    </div>
                  </td>
                  <td style="text-align: center">
                    <!-- แสดงไอคอนติ๊กถูกแทนข้อความ -->
                    <v-icon :color="item.continueQuestion ? 'success' : 'grey-lighten-2'" :class="{ 'icon-active': item.continueQuestion, 'icon-inactive': !item.continueQuestion }" size="20">
                      mdi-check-circle
                    </v-icon>
                  </td>
                  <td style="text-align: center">
                    <!-- #928 แสดง screen 2 ชิปแรก ที่เหลือรวมเป็นชิป "+N" (hover/คลิกเพื่อดูทั้งหมด) -->
                    <div class="data-container">
                      <v-chip v-for="(screen, index) in visibleChips(item.screen).shown" :key="index" size="small" variant="tonal" color="grey" class="data-chip ma-1" style="max-width: 150px;">
                        <v-tooltip activator="parent" location="top">
                          {{ getScreenName(screen) }}
                        </v-tooltip>
                        <span style="overflow: hidden; text-overflow: ellipsis; white-space: nowrap; display: block;">
                          {{ getScreenName(screen) }}
                        </span>
                      </v-chip>
                      <v-menu v-if="visibleChips(item.screen).hiddenCount > 0" open-on-hover open-on-click :close-on-content-click="false" location="bottom">
                        <template v-slot:activator="{ props }">
                          <v-chip v-bind="props" size="small" variant="tonal" color="grey" class="data-chip-more ma-1">
                            +{{ visibleChips(item.screen).hiddenCount }}
                          </v-chip>
                        </template>
                        <v-card class="chip-list-card">
                          <div class="chip-list-title">Screen ({{ item.screen.length }})</div>
                          <div class="chip-list-grid">
                            <div v-for="(screen, index) in item.screen" :key="index" class="chip-list-item">
                              {{ getScreenName(screen) }}
                            </div>
                          </div>
                        </v-card>
                      </v-menu>
                    </div>
                  </td>
                  <td style="text-align: center">
                    <span>{{ item.targetGroup }}</span>
                    <span v-if="item.targetGroup === 'CIF' && item.cifCount > 0" class="ml-1 text-caption text-grey">
                      ({{ item.cifCount }} CIFs)
                      <v-chip :color="getCifProcessingStatusColor(item.cifProcessingStatus)" size="small" variant="tonal">
                        {{ item.cifProcessingStatus }}
                      </v-chip>
                    </span>
                    
                  </td>
                  <td style="text-align: center">
                    <!-- แสดง segment เป็นกล่อง -->
                    <div class="data-container">
                      <v-chip v-for="(seg, index) in item.segment" :key="index" size="small" variant="tonal" color="grey" class="data-chip ma-1" style="max-width: 150px;">
                        <v-tooltip activator="parent" location="top">
                          {{ seg }}
                        </v-tooltip>
                        <span style="overflow: hidden; text-overflow: ellipsis; white-space: nowrap; display: block;">
                          {{ seg }}
                        </span>
                      </v-chip>
                    </div>
                  </td>
                  <td style="text-align: center">
                    <!-- แสดง wealth membership เป็นกล่อง โดยแสดง Membership name แทน WealthFlag -->
                    <div class="data-container">
                      <v-chip v-for="(flag, index) in item.wealthMembership" :key="index" size="small" variant="tonal" color="grey" class="data-chip ma-1" style="max-width: 150px;">
                        <v-tooltip activator="parent" location="top">
                          {{ getWealthMembershipName(flag) }}
                        </v-tooltip>
                        <span style="overflow: hidden; text-overflow: ellipsis; white-space: nowrap; display: block;">
                          {{ getWealthMembershipName(flag) }}
                        </span>
                      </v-chip>
                    </div>
                  </td>
                  <td style="text-align: center">{{ item.priority }}</td>
                  <td style="text-align: center">{{ item.effStartDate }}</td>
                  <td style="text-align: center">{{ item.effEndDate }}</td>
                  <td style="text-align: center">{{ item.createDate }}</td>
                  <td class="sticky-status" style="text-align: center">
                    <v-chip :color="item.status === 'Active' ? 'success' : 'error'" size="small">
                      {{ item.status }}
                    </v-chip>
                  </td>
                  <td class="sticky-repeat" style="text-align: center">
                    <!-- แสดงไอคอนติ๊กถูกแทนข้อความ -->
                    <v-icon :color="item.repeatFlag ? 'success' : 'grey-lighten-2'" :class="{ 'icon-active': item.repeatFlag, 'icon-inactive': !item.repeatFlag }" size="20"> mdi-check-circle </v-icon>
                  </td>
                  <td class="sticky-action" style="text-align: center">
                    <div class="action-group" style="justify-content: center; display: flex">
                      <v-btn variant="text" density="comfortable" @click="Edit(item)" class="action-btn edit-btn" icon>
                        <v-icon size="18">{{ permissions.edit ? 'mdi-pencil-outline' : 'mdi-eye-outline' }}</v-icon>
                        <v-tooltip activator="parent" location="top">{{ permissions.edit ? 'Edit' : 'View' }}</v-tooltip>
                      </v-btn>
                      <v-btn v-if="permissions.delete" variant="text" density="comfortable" @click="DeleteGroup(item.id)" class="action-btn delete-btn" icon>
                        <v-icon size="18">mdi-delete-outline</v-icon>
                        <v-tooltip activator="parent" location="top">Delete</v-tooltip>
                      </v-btn>
                    </div>
                  </td>
                </tr>
              </template>
            </v-data-table-server>
          </div>
        </v-card-item>
      </v-card>
    </v-container>

    <!-- Add/Edit Proactive Message Dialog -->
    <v-dialog v-model="addDialog" max-width="1200px" persistent>
      <v-card class="add-proactive-dialog">
        <!-- Header Section -->
        <div class="dialog-header dialog-header-add">
          <div class="header-main">
            <div class="header-icon-container">
              <div class="icon-background">
                <v-icon color="#135EB8" size="28">mdi-account</v-icon>
              </div>
            </div>
            <div class="header-text-content">
              <h2 class="dialog-title">{{ isEditMode ? (permissions.edit ? 'Edit Proactive' : 'View Proactive') : 'Add Proactive' }}</h2>
              <p class="dialog-subtitle">{{ isEditMode ? (permissions.edit ? 'Edit proactive message' : 'View proactive message') : 'Create a new proactive message' }}</p>
            </div>
          </div>
          <div class="header-actions">
            <v-btn icon variant="text" @click="closeAddDialog" class="close-button" color="#1F1F1F">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </div>
        </div>

        <v-divider></v-divider>

        <v-card-text class="dialog-content">
          <v-form ref="addForm" v-model="formValid">
            <!-- ══ Name ══════════════════════════════════════════════════════ -->
            <div class="mk-block">
              <label class="mk-label">Name</label>
              <v-text-field v-model="formData.name" placeholder="e.g. Tax Season Reminder"
                variant="outlined" rounded class="mk-field" :rules="[rules.required]" bg-color="white" hide-details="auto"></v-text-field>
            </div>

            <!-- ══ Proactive Message ═════════════════════════════════════════ -->
            <div class="mk-card-gray">
              <p class="mk-card-title">Proactive Message</p>
              <div class="mk-block">
                <label class="mk-label mk-label-sm"><img :src="images.ThaiIcon" class="mk-flag" alt="TH" /> In Thai</label>
                <v-text-field v-model="formData.messageThai" placeholder="ข้อความเชิงรุกภาษาไทย"
                  variant="outlined" rounded class="mk-field" :rules="[rules.required]" bg-color="white" hide-details="auto"></v-text-field>
              </div>
              <div class="mk-block">
                <label class="mk-label mk-label-sm"><img :src="images.EngIcon" class="mk-flag" alt="EN" /> In English</label>
                <v-text-field v-model="formData.messageEnglish" placeholder="Proactive message in English"
                  variant="outlined" rounded class="mk-field" :rules="[rules.required]" bg-color="white" hide-details="auto"></v-text-field>
              </div>
              <div class="mk-block">
                <label class="mk-label mk-label-sm"><img :src="images.ChaineseIcon" class="mk-flag" alt="CN" /> In Chinese</label>
                <v-text-field v-model="formData.messageChinese" placeholder="中文提示信息"
                  variant="outlined" rounded class="mk-field" :rules="[rules.required]" bg-color="white" hide-details="auto"></v-text-field>
              </div>
            </div>

            <!-- ══ Genie AI Character ════════════════════════════════════════ -->
            <div class="mk-card-outline">
              <label class="mk-check-head">
                <input type="checkbox" v-model="formData.genieCharacterEnabled" class="mk-checkbox" />
                <span>Genie AI Character</span>
              </label>
              <div class="mk-sub" :class="{ 'is-off': !formData.genieCharacterEnabled }">
                <label class="mk-label mk-label-xs">Character</label>
                <div class="mk-select-wrap">
                  <select v-model="formData.genieCharacter" class="mk-select" :disabled="!formData.genieCharacterEnabled">
                    <option :value="null" disabled>Select character</option>
                    <option v-for="opt in withRemovedOptions(genieCharacterOptions, formData.genieCharacter, unresolvedGenieCharacter ? [unresolvedGenieCharacter] : [])"
                      :key="opt.valueString" :value="opt.valueString">{{ opt.name }}</option>
                  </select>
                  <svg class="mk-caret" width="12" height="12" viewBox="0 0 24 24" fill="none"><path d="M6 9l6 6 6-6" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg>
                </div>
                <div v-if="unresolvedGenieCharacter" class="mk-warn">
                  <v-icon size="13" color="#B45309">mdi-alert-outline</v-icon>
                  This character is no longer Active and does not have "Show In Proactive Mode" on. It is kept until you change it.
                </div>
              </div>
            </div>

            <!-- ══ Action Type ═══════════════════════════════════════════════ -->
            <div class="mk-card-outline">
              <label class="mk-check-head">
                <input type="checkbox" v-model="formData.actionTypeEnabled" class="mk-checkbox" />
                <span>Action Type</span>
              </label>

              <div class="mk-sub" :class="{ 'is-off': !formData.actionTypeEnabled }">
                <p class="mk-eyebrow">Choose Action</p>
                <div class="mk-row-3">
                  <label class="mk-radio-row" :class="{ 'is-checked': formData.actionTypeEnabled && formData.actionType === 'ContinueQuestion', 'is-disabled': !formData.actionTypeEnabled }">
                    <input type="radio" value="ContinueQuestion" v-model="formData.actionType" :disabled="!formData.actionTypeEnabled" />
                    <span>Continue Question</span>
                  </label>
                  <label class="mk-radio-row" :class="{ 'is-checked': formData.actionTypeEnabled && formData.actionType === 'Action', 'is-disabled': !formData.actionTypeEnabled }">
                    <input type="radio" value="Action" v-model="formData.actionType" :disabled="!formData.actionTypeEnabled" />
                    <span>Action</span>
                  </label>
                  <label class="mk-radio-row" :class="{ 'is-checked': formData.actionTypeEnabled && formData.actionType === 'URL', 'is-disabled': !formData.actionTypeEnabled }">
                    <input type="radio" value="URL" v-model="formData.actionType" :disabled="!formData.actionTypeEnabled" />
                    <span>URL</span>
                  </label>
                </div>

                <!-- Choice: Continue Question -->
                <div v-if="formData.actionType === 'ContinueQuestion'" class="mk-fade mk-pt">
                  <p class="mk-card-title mk-mb">Continue Question</p>
                  <div class="mk-questions">
                    <div v-for="(question, index) in formData.continueQuestions" :key="index" class="mk-question">
                      <div class="mk-question-head">
                        <p class="mk-card-title">Question {{ index + 1 }}</p>
                        <button type="button" class="mk-icon-btn" @click="removeContinueQuestion(index)" title="Remove question">
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none"><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/></svg>
                        </button>
                      </div>
                      <div class="mk-block">
                        <label class="mk-label mk-label-sm"><img :src="images.ThaiIcon" class="mk-flag" alt="TH" /> In Thai</label>
                        <v-text-field v-model="question.questionThai" placeholder="คำถามภาษาไทย" variant="outlined" rounded class="mk-field" bg-color="white" hide-details></v-text-field>
                      </div>
                      <div class="mk-block">
                        <label class="mk-label mk-label-sm"><img :src="images.EngIcon" class="mk-flag" alt="EN" /> In English</label>
                        <v-text-field v-model="question.questionEnglish" placeholder="Question in English" variant="outlined" rounded class="mk-field" bg-color="white" hide-details></v-text-field>
                      </div>
                      <div class="mk-block">
                        <label class="mk-label mk-label-sm"><img :src="images.ChaineseIcon" class="mk-flag" alt="CN" /> In Chinese</label>
                        <v-text-field v-model="question.questionChinese" placeholder="中文问题" variant="outlined" rounded class="mk-field" bg-color="white" hide-details></v-text-field>
                      </div>

                      <!-- Quick Reply -->
                      <div class="mk-quick">
                        <div class="mk-quick-head">
                          <label class="mk-check-head mk-check-head-sm">
                            <input type="checkbox" v-model="question.quickReplyEnabled" class="mk-checkbox" @change="onQuickReplyToggle(question)" />
                            <span>Quick Reply</span>
                          </label>
                          <button type="button" class="mk-btn-sm" :disabled="!question.quickReplyEnabled" @click="addQuickReply(question)">
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none"><path d="M12 5v14M5 12h14" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"/></svg>
                            Add Choice
                          </button>
                        </div>
                        <div class="mk-quick-body">
                          <p v-if="!question.quickReplyEnabled" class="mk-quick-empty">Check the box above to enable Quick Reply choices.</p>
                          <p v-else-if="!question.quickReplies || question.quickReplies.length === 0" class="mk-quick-empty">No quick reply choices added yet.</p>
                          <div v-for="(reply, replyIndex) in question.quickReplies" :key="replyIndex" class="mk-choice mk-fade">
                            <div class="mk-choice-head">
                              <span class="mk-choice-label">Choice #{{ replyIndex + 1 }}</span>
                              <button type="button" class="mk-icon-btn" @click="removeQuickReply(question, replyIndex)" title="Remove choice">
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none"><path d="M4 7h16M9 7V4h6v3M6 7l1 13h10l1-13" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"/></svg>
                              </button>
                            </div>
                            <div class="mk-choice-grid">
                              <div>
                                <label class="mk-label mk-label-xxs"><img :src="images.ThaiIcon" class="mk-flag-xs" alt="TH" /> Thai</label>
                                <input type="text" v-model="reply.textThai" class="mk-input mk-input-xs" placeholder="ข้อความตอบกลับ" />
                              </div>
                              <div>
                                <label class="mk-label mk-label-xxs"><img :src="images.EngIcon" class="mk-flag-xs" alt="EN" /> English</label>
                                <input type="text" v-model="reply.textEnglish" class="mk-input mk-input-xs" placeholder="Reply text" />
                              </div>
                              <div>
                                <label class="mk-label mk-label-xxs"><img :src="images.ChaineseIcon" class="mk-flag-xs" alt="CN" /> Chinese</label>
                                <input type="text" v-model="reply.textChinese" class="mk-input mk-input-xs" placeholder="回复文本" />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <button type="button" class="mk-btn-primary" @click="addContinueQuestion">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M12 5v14M5 12h14" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"/></svg>
                    Add Question
                  </button>
                </div>

                <!-- Choice: Action -->
                <div v-else-if="formData.actionType === 'Action'" class="mk-fade mk-pt">
                  <label class="mk-label mk-label-xs">Action</label>
                  <div class="mk-action-row">
                    <div class="mk-select-wrap mk-action-select">
                      <select v-model="formData.actionName" class="mk-select" :disabled="!formData.actionTypeEnabled">
                        <option :value="null" disabled>Select action</option>
                        <option v-for="opt in withRemovedOptions(actionOptions, formData.actionName, unresolvedActionName ? [unresolvedActionName] : [])"
                          :key="opt.valueString" :value="opt.valueString">{{ opt.name }}</option>
                      </select>
                      <svg class="mk-caret" width="12" height="12" viewBox="0 0 24 24" fill="none"><path d="M6 9l6 6 6-6" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg>
                    </div>
                    <button type="button" class="mk-btn-sm mk-add-action" :disabled="!formData.actionTypeEnabled || !canEdit"
                      @click="showSuggestionActionManager = true">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none"><path d="M12 5v14M5 12h14" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"/></svg>
                      Add Action
                    </button>
                  </div>
                  <div v-if="unresolvedActionName" class="mk-warn">
                    <v-icon size="13" color="#B45309">mdi-alert-outline</v-icon>
                    This action no longer exists. It is kept until you change it.
                  </div>
                </div>

                <!-- Choice: URL -->
                <div v-else-if="formData.actionType === 'URL'" class="mk-fade mk-pt">
                  <label class="mk-label mk-label-xs">URL</label>
                  <input type="text" v-model="formData.actionUrl" class="mk-input" placeholder="https://example.com/..." :disabled="!formData.actionTypeEnabled" />
                </div>
              </div>
            </div>

            <!-- ══ Screen & Priority ═════════════════════════════════════════ -->
            <div class="mk-grid-2">
              <div class="mk-block">
                <label class="mk-label">Screen</label>
                <v-select v-model="formData.screen" :items="screenOptions" item-title="name" item-value="valueString"
                  placeholder="Select screen" variant="outlined" rounded class="mk-field" multiple chips hide-details="auto"></v-select>
              </div>
              <div class="mk-block">
                <label class="mk-label">Priority</label>
                <v-text-field v-model="formData.priority" type="number" min="0" step="1" placeholder="1"
                  variant="outlined" rounded class="mk-field" :rules="[rules.priority]" bg-color="white" hide-details="auto"></v-text-field>
              </div>
            </div>

            <!-- ══ Effective Dates ═══════════════════════════════════════════ -->
            <div class="mk-grid-2">
              <div class="mk-block">
                <label class="mk-label">Effective Start Date &amp; Time</label>
                <v-text-field v-model="formData.effectiveStartDate" type="datetime-local" variant="outlined" rounded
                  class="mk-field" :rules="[rules.required]" bg-color="white" hide-details="auto"></v-text-field>
              </div>
              <div class="mk-block">
                <label class="mk-label">Effective End Date &amp; Time</label>
                <v-text-field v-model="formData.effectiveEndDate" type="datetime-local" variant="outlined" rounded
                  class="mk-field" :rules="endDateRules" clearable bg-color="white" hide-details="auto"></v-text-field>
              </div>
            </div>

            <!-- ══ Display Days ══════════════════════════════════════════════ -->
            <div class="mk-block">
              <label class="mk-label">Display Days</label>
              <!-- Birthday is its own schedule: once it is on, the weekday presets and pills stay on
                   screen but are disabled, and the day list is cleared, so the backend consults only
                   the birthday rule. -->
              <div class="mk-preset-row">
                <button type="button" class="mk-preset" :class="{ 'is-active': isDayPresetActive('everyday') }" :disabled="formData.birthdayEnabled" @click="applyDayPreset('everyday')">Everyday</button>
                <button type="button" class="mk-preset" :class="{ 'is-active': isDayPresetActive('weekday') }" :disabled="formData.birthdayEnabled" @click="applyDayPreset('weekday')">Every Weekday</button>
                <button type="button" class="mk-preset" :class="{ 'is-active': isDayPresetActive('weekend') }" :disabled="formData.birthdayEnabled" @click="applyDayPreset('weekend')">Every Weekend</button>
                <button type="button" class="mk-day-pill mk-day-pill-wide" :class="{ 'is-checked': formData.birthdayEnabled }" @click="toggleBirthday">Birthday</button>
              </div>
              <div class="mk-day-row">
                <button type="button" v-for="day in displayDayItems" :key="day.value"
                  class="mk-day-pill" :class="{ 'is-checked': isDisplayDaySelected(day.value) }"
                  :disabled="formData.birthdayEnabled"
                  @click="toggleDisplayDay(day.value)">{{ day.label }}</button>
              </div>
              <p v-if="!formData.birthdayEnabled" class="mk-hint">No day selected means the message shows every day.</p>

              <div v-if="formData.birthdayEnabled" class="mk-birthday mk-fade">
                <label class="mk-check-head mk-check-head-sm">
                  <input type="checkbox" v-model="formData.beforeBirthdayEnabled" class="mk-checkbox" />
                  <span class="mk-birthday-inline">
                    Before Birthday
                    <input type="number" min="1" v-model="formData.beforeBirthdayDays" class="mk-input mk-input-tiny"
                      placeholder="e.g. 3" :disabled="!formData.beforeBirthdayEnabled" @click.stop />
                    Days
                  </span>
                </label>
                <label class="mk-check-head mk-check-head-sm">
                  <input type="checkbox" v-model="formData.onBirthdayEnabled" class="mk-checkbox" />
                  <span>On Birthday</span>
                </label>
                <p class="mk-hint">Birthday replaces the weekday schedule &mdash; the message shows only on the birthday rules above.</p>
              </div>
            </div>

            <!-- ══ Display Period ════════════════════════════════════════════ -->
            <div class="mk-block">
              <label class="mk-label">Display Period</label>
              <div class="mk-row-2">
                <label class="mk-radio-row" :class="{ 'is-checked': formData.displayPeriod === 'AllDay' }">
                  <input type="radio" value="AllDay" v-model="formData.displayPeriod" />
                  <span>All Day</span>
                </label>
                <label class="mk-radio-row" :class="{ 'is-checked': formData.displayPeriod === 'SpecificPeriod' }">
                  <input type="radio" value="SpecificPeriod" v-model="formData.displayPeriod" />
                  <span>Specific Period</span>
                </label>
              </div>
              <div v-if="formData.displayPeriod === 'SpecificPeriod'" class="mk-grid-2 mk-fade mk-mt">
                <div>
                  <label class="mk-label mk-label-xs">Start Time</label>
                  <input type="time" v-model="formData.displayStartTime" class="mk-input" />
                </div>
                <div>
                  <label class="mk-label mk-label-xs">End Time</label>
                  <input type="time" v-model="formData.displayEndTime" class="mk-input" />
                </div>
              </div>
              <p v-if="formData.displayPeriod === 'SpecificPeriod'" class="mk-hint">
                Customer local time (Asia/Bangkok). A period may cross midnight, e.g. 22:00 to 02:00.
              </p>
            </div>

            <!-- ══ Target Group ══════════════════════════════════════════════ -->
            <div class="mk-card-gray">
              <label class="mk-label mk-mb0">Target Group</label>

              <div>
                <p class="mk-heading">1. Primary Segment</p>
                <div class="mk-row-4">
                  <label class="mk-seg-row" :class="{ 'is-checked': formData.targetGroup === 'All' }">
                    <input type="radio" value="All" v-model="formData.targetGroup" />
                    <span>All</span>
                  </label>
                  <label class="mk-seg-row" :class="{ 'is-checked': formData.targetGroup === 'Persona' }">
                    <input type="radio" value="Persona" v-model="formData.targetGroup" />
                    <span>Persona</span>
                  </label>
                  <label class="mk-seg-row" :class="{ 'is-checked': formData.targetGroup === 'CIF' }">
                    <input type="radio" value="CIF" v-model="formData.targetGroup" />
                    <span>CIF List</span>
                  </label>
                  <label class="mk-seg-row" :class="{ 'is-checked': formData.targetGroup === 'ExceptCIF' }">
                    <input type="radio" value="ExceptCIF" v-model="formData.targetGroup" />
                    <span>Except CIF</span>
                  </label>
                </div>

                <!-- Persona panel -->
                <div v-if="formData.targetGroup === 'Persona'" class="mk-fade mk-mt">
                  <label class="mk-label mk-label-xs">Segment</label>
                  <v-select v-model="formData.segment" :items="segmentOptions" item-title="name" item-value="valueString"
                    placeholder="Select Segment" variant="outlined" rounded class="mk-field" multiple chips hide-details></v-select>
                  <label class="mk-label mk-label-xs mk-mt">Wealth Membership</label>
                  <v-select v-model="formData.wealthMembership" :items="wealthMembershipOptions" item-title="name" item-value="valueString"
                    placeholder="Select Wealth Membership" variant="outlined" rounded class="mk-field" multiple chips hide-details></v-select>
                  <p class="mk-hint">
                    Choose at least one Segment or Wealth Membership — leaving both empty would send this
                    message to everyone, which is what Target Group &ldquo;All&rdquo; is for.
                  </p>
                </div>

                <!-- CIF panel: same controls for CIF (include) and ExceptCIF (block) -->
                <div v-if="usesCifList" class="mk-fade mk-mt">
                  <p class="mk-hint mk-mb">
                    {{ formData.targetGroup === 'ExceptCIF'
                       ? 'These CIFs will be BLOCKED from receiving this message.'
                       : 'Only these CIFs will receive this message.' }}
                  </p>
                  <div class="mk-cif-actions">
                    <button type="button" class="mk-btn-upload" @click="triggerFileUpload">
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none"><rect x="3" y="3" width="18" height="18" rx="2.5" stroke="currentColor" stroke-width="1.8"/><path d="M8 13l2.2 2.2L16 9" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>
                      Upload File
                    </button>
                    <button type="button" class="mk-btn-download" @click="downloadTemplate">
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none"><rect x="3" y="3" width="18" height="18" rx="2.5" stroke="#B9F3DA" stroke-width="1.8"/><path d="M8 8h8M8 12h8M8 16h5" stroke="#B9F3DA" stroke-width="1.8" stroke-linecap="round"/></svg>
                      Download Template
                    </button>
                  </div>
                </div>
              </div>

              <input ref="fileInput" type="file" accept=".csv,.xlsx,.xls" style="display: none" @change="handleFileUpload" />

              <!-- Uploaded files -->
              <div v-if="usesCifList && hasUploadedFiles" class="uploaded-files-info mt-4">
                <v-card variant="outlined" class="file-table-card">
                  <v-card-title class="py-3 px-4 bg-grey-lighten-5">
                    <v-icon class="mr-2" color="#4A2E80">mdi-file-table</v-icon>
                    Uploaded CIF Files ({{ totalUploadedFiles }}) - Total CIF Codes: {{ totalCifCodes }}
                  </v-card-title>
                  <v-table class="file-table">
                    <thead>
                      <tr>
                        <th class="text-left font-weight-bold">File Name</th>
                        <th class="text-center font-weight-bold" style="width: 120px;">CIF Count</th>
                        <th class="text-center font-weight-bold" style="width: 120px;">Upload Date</th>
                        <th class="text-center font-weight-bold" style="width: 200px;">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(file, index) in uploadedFiles" :key="index">
                        <td class="py-3">
                          <div class="d-flex align-center">
                            <v-icon class="mr-2" color="success">mdi-file-document</v-icon>
                            <div>
                              <div class="file-name">{{ file.fileName }}</div>
                              <div class="text-caption text-grey">{{ formatFileSize(file.fileSize) }}</div>
                            </div>
                          </div>
                        </td>
                        <td class="text-center py-3">
                          <v-chip size="small" color="#4A2E80" variant="outlined">{{ file.cifCount }}</v-chip>
                        </td>
                        <td class="text-center py-3">
                          <div class="text-caption">{{ formatUploadDate(file.uploadDate) }}</div>
                        </td>
                        <td class="text-center py-3">
                          <div class="action-buttons-group">
                            <v-btn variant="outlined" color="#4A2E80" size="small" class="mr-2" @click="downloadCifFile(file)">
                              <v-icon size="16" class="mr-1">mdi-download</v-icon> Download
                            </v-btn>
                            <v-btn variant="outlined" color="error" size="small" @click="removeCifFile(index)">
                              <v-icon size="16" class="mr-1">mdi-delete</v-icon> Delete
                            </v-btn>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </v-table>
                  <v-card-actions class="py-2 px-4" v-if="uploadedFiles.length > 1">
                    <v-spacer></v-spacer>
                    <v-btn variant="outlined" color="error" size="small" @click="clearAllFiles">
                      <v-icon size="16" class="mr-1">mdi-delete-sweep</v-icon> Clear All Files
                    </v-btn>
                  </v-card-actions>
                </v-card>
              </div>

              <div class="mk-divider-dashed"></div>

              <p class="mk-heading">2. Refinement Conditions (AND Logic)</p>

              <!-- Tax Planning condition -->
              <div class="mk-card-white">
                <label class="mk-check-head">
                  <input type="checkbox" v-model="formData.taxConditionEnabled" class="mk-checkbox" />
                  <span>Tax Planning</span>
                </label>
                <p class="mk-hint">
                  Enabling this also marks the message &ldquo;For Tax Planning Mode&rdquo;, which limits it to
                  customers who have Tax Planning available.
                </p>

                <div class="mk-sub" :class="{ 'is-off': !formData.taxConditionEnabled }">
                  <p class="mk-eyebrow">Tax Planning Status Type</p>

                  <label class="mk-radio-row" :class="{ 'is-checked': formData.taxConditionEnabled && formData.taxConditionStatusType === 'Never', 'is-disabled': !formData.taxConditionEnabled }">
                    <input type="radio" value="Never" v-model="formData.taxConditionStatusType" :disabled="!formData.taxConditionEnabled" />
                    <span>Never Create Tax Planning</span>
                  </label>

                  <div>
                    <label class="mk-radio-row" :class="{ 'is-checked': formData.taxConditionEnabled && formData.taxConditionStatusType === 'NeverInYears', 'is-disabled': !formData.taxConditionEnabled }">
                      <input type="radio" value="NeverInYears" v-model="formData.taxConditionStatusType" :disabled="!formData.taxConditionEnabled" />
                      <span>Never Create in Specific Tax Year(s)</span>
                    </label>
                    <div v-if="formData.taxConditionEnabled && formData.taxConditionStatusType === 'NeverInYears'" class="mk-fade mk-nest">
                      <label class="mk-label mk-label-xs">Tax Year</label>
                      <div class="mk-year-grid">
                        <label v-for="y in taxYearTiles" :key="y.valueNumber" class="mk-check-tile" :class="{ 'is-checked': formData.taxConditionYears.includes(y.valueNumber), 'is-removed': y.removed }">
                          <input type="checkbox" :value="y.valueNumber" v-model="formData.taxConditionYears" class="mk-checkbox" />
                          {{ y.name }}
                        </label>
                      </div>
                      <div v-if="taxYearHint" class="mk-warn">
                        <v-icon size="13" color="#B45309">mdi-alert-outline</v-icon>
                        {{ taxYearHint }}
                      </div>
                      <div v-if="removedTaxConditionYears.length" class="mk-warn">
                        <v-icon size="13" color="#B45309">mdi-alert-outline</v-icon>
                        {{ removedTaxConditionYears.join(', ') }} not in Tax Config — untick it and pick a tax year from the list.
                      </div>
                    </div>
                  </div>

                  <div>
                    <label class="mk-radio-row" :class="{ 'is-checked': formData.taxConditionEnabled && formData.taxConditionStatusType === 'PendingInState', 'is-disabled': !formData.taxConditionEnabled }">
                      <input type="radio" value="PendingInState" v-model="formData.taxConditionStatusType" :disabled="!formData.taxConditionEnabled" />
                      <span>Pending in Specific State</span>
                    </label>
                    <div v-if="formData.taxConditionEnabled && formData.taxConditionStatusType === 'PendingInState'" class="mk-fade mk-nest">
                      <label class="mk-label mk-label-xs">Tax Year</label>
                      <div class="mk-year-grid mk-mb">
                        <label v-for="y in taxYearTiles" :key="y.valueNumber" class="mk-check-tile" :class="{ 'is-checked': formData.taxConditionYears.includes(y.valueNumber), 'is-removed': y.removed }">
                          <input type="checkbox" :value="y.valueNumber" v-model="formData.taxConditionYears" class="mk-checkbox" />
                          {{ y.name }}
                        </label>
                      </div>
                      <div v-if="taxYearHint" class="mk-warn mk-mb">
                        <v-icon size="13" color="#B45309">mdi-alert-outline</v-icon>
                        {{ taxYearHint }}
                      </div>
                      <div v-if="removedTaxConditionYears.length" class="mk-warn mk-mb">
                        <v-icon size="13" color="#B45309">mdi-alert-outline</v-icon>
                        {{ removedTaxConditionYears.join(', ') }} not in Tax Config — untick it and pick a tax year from the list.
                      </div>
                      <!-- Group states — the same four the app bar offers (BarConditionState via taxStateOptions),
                           matched against TaxFormV2.CurrentStateGroup. Never hardcode the list here. -->
                      <label class="mk-label mk-label-xs">Current State</label>
                      <div class="mk-state-grid">
                        <label v-for="st in withRemovedOptions(taxStateOptions, formData.taxConditionStates, removedTaxConditionStates)" :key="st.valueString"
                          class="mk-check-tile" :class="{ 'is-checked': formData.taxConditionStates.includes(st.valueString), 'is-removed': st.removed }">
                          <input type="checkbox" :value="st.valueString" v-model="formData.taxConditionStates" class="mk-checkbox" />
                          {{ st.name }}
                        </label>
                      </div>
                      <!-- A checkbox list cannot carry a rule, so the empty case is called out here, as on
                           Bar Management. The backend rejects it too (ValidateActionAndConditionsAsync). -->
                      <div v-if="formData.taxConditionStates.length === 0" class="mk-warn">
                        <v-icon size="13" color="#B45309">mdi-alert-outline</v-icon>
                        Please select at least one
                      </div>
                      <div v-if="removedTaxConditionStates.length" class="mk-warn">
                        <v-icon size="13" color="#B45309">mdi-alert-outline</v-icon>
                        {{ removedTaxConditionStates.join(', ') }}
                        {{ removedTaxConditionStates.length === 1 ? 'is not a state group' : 'are not state groups' }}
                        — untick it and pick a state group instead.
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </v-form>
        </v-card-text>

        <v-divider></v-divider>

        <!-- Dialog Actions with Toggle Switches -->
        <v-card-actions class="dialog-actions pa-4">
          <!-- Toggle Switches Section - ชิดซ้าย -->
          <div class="toggle-switches-section">
            <v-row align="center" no-gutters>
              <v-col cols="auto" class="mr-4">
                <!-- Locked only while ON: Default forbids turning Enable off, never turning it on. -->
                <v-switch v-model="formData.enableFlag" color="primary" :disabled="formData.defaultFlag && formData.enableFlag" :label="formData.enableFlag ? 'Enable' : 'Disable'" hide-details density="compact" class="custom-switch"></v-switch>
              </v-col>
              <v-col cols="auto" class="mr-4">
                <v-switch v-model="formData.repeatFlag" color="primary" :label="formData.repeatFlag ? 'Repeat Flag' : 'No Repeat'" hide-details density="compact" class="custom-switch"></v-switch>
              </v-col>
              <!-- 🔴 Locked only while OFF: Enable forbids turning Default on, never turning it off. A row saved
                   before the Default⇒Enable rule (Default on + Enable off) used to load with BOTH switches
                   locked and every save rejected; now either switch can be flipped to fix it. -->
              <v-col cols="auto">
                <v-switch v-model="formData.defaultFlag" color="primary" :disabled="!formData.enableFlag && !formData.defaultFlag" :label="formData.defaultFlag ? 'Default Flag' : 'No Default'" hide-details density="compact" class="custom-switch"></v-switch>
              </v-col>
            </v-row>
          </div>

          <!-- Spacer เพื่อผลักปุ่มไปขวา -->
          <v-spacer></v-spacer>

          <!-- Action Buttons - ชิดขวา -->
          <div class="action-buttons">
            <v-btn color="grey" variant="outlined" @click="closeAddDialog" class="btn-close"> <v-icon>mdi-close</v-icon> {{ isViewOnly ? 'Close' : 'Cancel' }} </v-btn>
            <v-btn v-if="isEditMode ? permissions.edit : permissions.add" color="primary" @click="saveProactiveMessage" dark class="btn-save"> <v-icon>mdi-content-save</v-icon> {{ isEditMode ? 'Update' : 'Save' }} </v-btn>
          </div>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Manage Suggestion Actions — the SAME master list the Mobile Configuration screens edit.
         🔴 is-tax-planning-mode MUST be false here: this screen's dropdown is populated from the
         LH Bank scope (ProactiveMessageBAL.GetActionOptionsAsync passes isTaxPlanningMode: false),
         so opening the manager in the tax-planning scope would let an admin create an action that
         never appears in the dropdown they opened it from. -->
    <SuggestionActionManagerDialog
      v-model="showSuggestionActionManager"
      :is-tax-planning-mode="false"
      :can-edit="canEdit"
      @saved="onSuggestionActionsSaved"
    />

    <!-- Filter Sidebar -->
    <v-navigation-drawer v-model="filterDrawer" location="right" temporary width="400" class="filter-drawer">
      <v-card flat class="filter-card">
        <v-card-title class="filter-header d-flex align-center justify-space-between">
          <div class="d-flex align-center">
            <v-icon class="mr-2" color="primary">mdi-tune</v-icon>
            <span>Filter</span>
          </div>
          <v-btn icon small @click="filterDrawer = false" variant="text">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-divider></v-divider>

        <v-card-text class="filter-content">
          <!-- Status Filter -->
          <div class="filter-section">
            <h4 class="filter-section-title">Status</h4>
            <v-checkbox v-model="filters.status.all" label="All" density="comfortable" class="filter-checkbox" @change="toggleAllStatus"></v-checkbox>
            <v-checkbox v-model="filters.status.active" label="Active" density="comfortable" class="filter-checkbox" @change="handleSpecificStatusChange"></v-checkbox>
            <v-checkbox v-model="filters.status.inactive" label="Inactive" density="comfortable" class="filter-checkbox" @change="handleSpecificStatusChange"></v-checkbox>
          </div>

          <v-divider class="my-4"></v-divider>

          <!-- Continue Question Filter -->
          <div class="filter-section">
            <h4 class="filter-section-title">Continue Question</h4>
            <v-checkbox v-model="filters.continueQuestion.all" label="All" density="comfortable" class="filter-checkbox" @change="toggleAllContinueQuestion"></v-checkbox>
            <v-checkbox v-model="filters.continueQuestion.yes" label="Yes" density="comfortable" class="filter-checkbox" @change="handleSpecificContinueQuestionChange"></v-checkbox>
            <v-checkbox v-model="filters.continueQuestion.no" label="No" density="comfortable" class="filter-checkbox" @change="handleSpecificContinueQuestionChange"></v-checkbox>
          </div>

          <v-divider class="my-4"></v-divider>

          <!-- Target Group Filter -->
          <div class="filter-section">
            <h4 class="filter-section-title">Target Group</h4>
            <v-checkbox v-model="filters.targetGroup.all" label="All" density="comfortable" class="filter-checkbox" @change="toggleAllTargetGroup"></v-checkbox>
            <v-checkbox v-model="filters.targetGroup.persona" label="Persona" density="comfortable" class="filter-checkbox" @change="handleSpecificTargetGroupChange"></v-checkbox>
            <v-checkbox v-model="filters.targetGroup.cif" label="CIF" density="comfortable" class="filter-checkbox" @change="handleSpecificTargetGroupChange"></v-checkbox>
          </div>

          <v-divider class="my-4"></v-divider>

          <!-- Screen Filter -->
          <div class="filter-section">
            <h4 class="filter-section-title">Screen</h4>
            <v-select
              v-model="filters.screen"
              :items="screenOptions"
              item-title="name"
              item-value="valueString"
              multiple
              chips
              label="Select Screen"
              variant="outlined"
              rounded
              class="search-field custom-rounded"
              :menu-props="{
                attach: 'body',
                zIndex: 9999,
                maxHeight: 300,
              }"
            ></v-select>
          </div>

          <v-divider class="my-4"></v-divider>

          <!-- Segment Filter -->
          <div class="filter-section">
            <h4 class="filter-section-title">Segment</h4>
            <v-select
              v-model="filters.segment"
              :items="segmentOptions"
              item-title="name"
              item-value="valueString"
              multiple
              chips
              label="Select Segment"
              variant="outlined"
              rounded
              class="search-field custom-rounded"
              :menu-props="{
                attach: 'body',
                zIndex: 9999,
                maxHeight: 300,
              }"
            ></v-select>
          </div>

          <v-divider class="my-4"></v-divider>

          <!-- Wealth Membership Filter -->
          <div class="filter-section">
            <h4 class="filter-section-title">Wealth Membership</h4>
            <v-select
              v-model="filters.wealthMembership"
              :items="wealthMembershipOptions"
              item-title="name"
              item-value="valueString"
              multiple
              chips
              label="Select Wealth Membership"
              variant="outlined"
              rounded
              class="search-field custom-rounded"
              :menu-props="{
                attach: 'body',
                zIndex: 9999,
                maxHeight: 300,
              }"
            ></v-select>
          </div>

          <v-divider class="my-4"></v-divider>

          <!-- Effective Start Date & Time Filter -->
          <div class="filter-section">
            <h4 class="filter-section-title">Effective Start Date &amp; Time</h4>
            <v-text-field v-model="filters.effectiveStartDate" type="datetime-local" label="Start Date &amp; Time" variant="outlined" rounded class="filter-field filter-rounded"></v-text-field>
          </div>

          <v-divider class="my-4"></v-divider>

          <!-- Effective End Date & Time Filter -->
          <div class="filter-section">
            <h4 class="filter-section-title">Effective End Date &amp; Time</h4>
            <v-text-field v-model="filters.effectiveEndDate" type="datetime-local" label="End Date &amp; Time" variant="outlined" rounded class="filter-field filter-rounded"></v-text-field>
          </div>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="filter-actions pa-4">
          <v-btn color="grey" variant="outlined" @click="clearFilters" class="flex-fill"> Clear All </v-btn>
          <v-btn color="primary" @click="applyFilters" class="flex-fill ml-2"> Apply Filter </v-btn>
        </v-card-actions>
      </v-card>
    </v-navigation-drawer>
  </div>
</template>
<script>
import checkPermissionTwo from '@/api/function.js';
import StatusIndicator from '@/components/StatusIndicator.vue';
import SuggestionActionManagerDialog from '@/components/SuggestionActionManagerDialog.vue';
import Cookies from 'js-cookie';
import dateUtils from '../plugins/date-utils';
import moment from 'moment';
import editImg from '@/assets/edit1.png';
import deleteImg from '@/assets/delete1.png';
import thaiIcon from '@/assets/images/thai-flag.svg';
import engIcon from '@/assets/images/eng-flag.svg';
import chaineseIcon from '@/assets/images/chainese-flag.svg';
import excelIcon from '@/assets/images/excel-icon.svg';
import {
  getProactiveCifClearMessage,
  shouldClearProactiveCifFiles,
} from '@/utils/proactiveCifTransition.js';

const PROACTIVE_MESSAGE_PROCESSING_STORAGE_KEY = 'proactiveMessageActiveProcessingId';

// Weekday tokens, in the order the backend's ProactiveMessageConditionMatcher expects.
// An EMPTY selection means every day — not "no days" — so the presets below never produce [].
const DISPLAY_DAYS = [
  { value: 'Mon', label: 'Mon' },
  { value: 'Tue', label: 'Tue' },
  { value: 'Wed', label: 'Wed' },
  { value: 'Thu', label: 'Thu' },
  { value: 'Fri', label: 'Fri' },
  { value: 'Sat', label: 'Sat' },
  { value: 'Sun', label: 'Sun' },
];

const DAY_PRESETS = {
  everyday: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
  weekday: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'],
  weekend: ['Sat', 'Sun'],
};

/**
 * The blank Add/Edit form.
 *
 * Defined ONCE and used by both data() and resetForm(). Those two used to carry separate copies
 * of this object literal, which is how a field ends up initialized in one place and undefined in
 * the other — the dialog then works on first open and breaks after a reset, or vice versa.
 */
function blankFormData() {
  return {
    name: '',
    messageThai: '',
    messageEnglish: '',
    messageChinese: '',
    priority: 1,
    effectiveStartDate: null,
    effectiveEndDate: null,
    targetGroup: 'All',
    screen: [],
    segment: [],
    wealthMembership: [],
    cifFiles: [],
    enableFlag: true,
    repeatFlag: false,
    defaultFlag: false,
    taxPlanningEnabled: false,
    continueQuestions: [],

    // ── Action Type ──
    // actionTypeEnabled is UI-only: the API models "section off" as actionType = null.
    actionTypeEnabled: false,
    actionType: 'ContinueQuestion',
    actionUrl: '',
    actionName: null,

    // ── Genie AI Character ──
    genieCharacterEnabled: false,
    genieCharacter: null,

    // ── Display Days / Birthday ──
    displayDays: [],
    birthdayEnabled: false,
    beforeBirthdayEnabled: false,
    beforeBirthdayDays: null,
    onBirthdayEnabled: false,

    // ── Display Period ──
    displayPeriod: 'AllDay',
    displayStartTime: null,
    displayEndTime: null,

    // ── Tax Planning refinement condition ──
    taxConditionEnabled: false,
    taxConditionStatusType: 'Never',
    taxConditionYears: [],
    taxConditionStates: [],
  };
}

export default {
  name: 'ProactiveMessage',
  inject: ['flagNav', 'FontColorTitle'],
  components: { StatusIndicator, SuggestionActionManagerDialog },
  mixins: [checkPermissionTwo],
  data: () => ({
    today: null,
    searchText: '',
    searchDebounceTimer: null, // Timer for debouncing search input
    loading: false,
    exportLoading: false, // Loading state for export
    dialog: false,
    filterDrawer: false,

    // Data table
    options: {
      page: 1,
      itemsPerPage: 10,
      sortBy: [],
      sortDesc: [],
    },
    items: [],
    searchTotalTable: 0,
    totalTable: 0,
    isFirst: true,
    isSearch: false,

    // Options from API
    options_data: {
      targetGroups: ['All', 'Persona', 'CIF'],
      screens: [],
      segments: [],
      wealthMemberships: [],
    },

    images: {
      EditImg: editImg,
      DeleteImg: deleteImg,
      ExcelImg: excelIcon,
      ThaiIcon: thaiIcon,
      EngIcon: engIcon,
      ChaineseIcon: chaineseIcon,
    },

    // Filter Sidebar
    filters: {
      status: {
        all: true,
        active: false,
        inactive: false,
      },
      continueQuestion: {
        all: true,
        yes: false,
        no: false,
      },
      targetGroup: {
        all: true,
        persona: false,
        cif: false,
      },
      screen: [],
      segment: [],
      wealthMembership: [],
      effectiveStartDate: null,
      effectiveEndDate: null,
    },
    screenOptions: ['Home screen', 'Deposit Shelf', 'Insurance Shelf'],
    segmentOptions: [],
    wealthMembershipOptions: [], // Changed to empty array, will be populated from API

    // Add/Edit Dialog
    addDialog: false,
    isEditMode: false,
    editingItemId: null,
    formValid: false,
    formData: blankFormData(),

    // Master lists for the Action & Conditions controls (GET /options)
    taxYearOptions: [],
    // Until the options call answers, an empty taxYearOptions means "not loaded", not "Tax Config has no year".
    taxOptionsLoaded: false,
    taxStateOptions: [],
    genieCharacterOptions: [],
    actionOptions: [],

    displayDayItems: DISPLAY_DAYS,
    dayPresets: DAY_PRESETS,

    // §17/§18 — stored values whose master-list entry is gone. Kept so the select can still
    // render them (flagged) instead of dropping the value on the floor.
    // True only while populateFormWithItem is assigning the form. The defaultFlag /
    // targetGroup watchers below exist to react to an ADMIN's click; during a load they would
    // instead "correct" values that came straight from the database.
    isPopulatingForm: false,

    showSuggestionActionManager: false,

    unresolvedActionName: null,
    unresolvedGenieCharacter: null,
    removedTaxConditionStates: [],

    // Form validation rules
    rules: {
      required: (value) => !!value || 'This field is required',
      priority: (value) => {
        if (value === null || value === undefined || value === '') return 'This field is required';
        const priority = Number(value);
        return (Number.isInteger(priority) && priority >= 0) || 'Priority must be a whole number of 0 or greater';
      },
    },

    // Uploaded files info (changed from single file to multiple files)
    uploadedFiles: [], // Array of CIF files: [{ fileName, filePath, cifList, uploadDate, fileSize }]
    currentUserId: Cookies.get('username') || sessionStorage.getItem('username') || Cookies.get('userId') || sessionStorage.getItem('userid') || '',
    activeProcessingSessionId: null,
    activeProcessingMessageId: null,
    activeProcessingStatusList: [],
    activeProcessingCompleted: false,
    processingStatusClearTimer: null,
  }),
  computed: {
    /**
     * Stored tax years that Tax Config (tax.TaxYearV2, via taxYearOptions) does not offer — e.g. a year
     * saved from the old V1 list. Without a tile it would stay ticked unseen and the API would reject
     * the save, so it is shown "(removed)" to be unticked, like removedTaxConditionStates.
     */
    removedTaxConditionYears() {
      if (!this.taxOptionsLoaded) return [];
      const known = new Set(this.taxYearOptions.map((y) => y.valueNumber));
      return (this.formData.taxConditionYears || []).filter((y) => !known.has(y));
    },
    taxYearTiles() {
      return [...this.taxYearOptions, ...this.removedTaxConditionYears.map((y) => ({ name: `${y} (removed)`, valueNumber: y, removed: true }))];
    },
    // One hint under the year grid: Tax Config has no year at all, or none is ticked yet.
    taxYearHint() {
      if (this.taxOptionsLoaded && this.taxYearOptions.length === 0) {
        return 'No tax year in Tax Config. Add one under Tax Config first.';
      }
      if ((this.formData.taxConditionYears || []).length === 0) {
        return 'Please select at least one';
      }
      return '';
    },
    /**
     * Both CIF-list target groups upload the same file. CIF makes it an include list, ExceptCIF
     * makes it a block list; the difference is decided server-side from TargetGroup, so every
     * upload/download/table control keys off this rather than testing for 'CIF' alone — that test
     * is exactly what would leave ExceptCIF with no way to attach the file it requires.
     */
    usesCifList() {
      return this.formData.targetGroup === 'CIF' || this.formData.targetGroup === 'ExceptCIF';
    },
    isViewOnly() {
      return this.$pagePermission && this.$pagePermission.isViewOnly('ProactiveMessage');
    },
    canAdd() {
      return !!(this.$pagePermission && this.$pagePermission.canAdd('ProactiveMessage'));
    },
    canEdit() {
      return !!(this.$pagePermission && this.$pagePermission.canEdit('ProactiveMessage'));
    },
    canDelete() {
      return !!(this.$pagePermission && this.$pagePermission.canDelete('ProactiveMessage'));
    },
    permissions() {
      return {
        add: this.$pagePermission && this.$pagePermission.canAdd('ProactiveMessage'),
        edit: this.$pagePermission && this.$pagePermission.canEdit('ProactiveMessage'),
        delete: this.$pagePermission && this.$pagePermission.canDelete('ProactiveMessage'),
      }
    },
    // Validation rule for End Date & Time. Optional — an empty end date means the message never
    // expires (same rule as Bar Management); when one is given it must be >= Start Date & Time.
    endDateRules() {
      return [
        (value) => {
          if (!value || !this.formData.effectiveStartDate) return true;
          return value >= this.formData.effectiveStartDate || 'End Date & Time must be on or after Start Date & Time';
        }
      ];
    },
    headers() {
      return [
        { key: 'number', sortable: false },
        { key: 'massage', sortable: true },
        { key: 'continueQuestion', sortable: true },
        { key: 'screen', sortable: true },
        { key: 'wealthMembership', sortable: false },
        { key: 'priority', sortable: false },
        { key: 'effStartDate', sortable: false },
        { key: 'effEndDate', sortable: false },
        { key: 'createDate', sortable: false },
        { key: 'status', sortable: false },
        { key: 'repeatFlag', sortable: false },
        { key: 'action', sortable: false },
      ];
    },
    
    // Total number of uploaded files
    totalUploadedFiles() {
      return this.uploadedFiles.length;
    },
    
    // Total CIF codes across all files
    totalCifCodes() {
      return this.uploadedFiles.reduce((total, file) => {
        return total + file.cifCount;
      }, 0);
    },
    
    // Check if any files are uploaded
    hasUploadedFiles() {
      return this.uploadedFiles.length > 0;
    },
  },
  watch: {
    options: {
      handler() {
        this.Get();
      },
      deep: true,
      immediate: true,
    },
    searchText: {
      handler(newValue) {
        this.debouncedSearch(newValue);
      },
    },
    'formData.defaultFlag': {
      handler(newValue) {
        // 🔴 Never fire while loading an existing message. Assigning formData wholesale trips this
        // watcher, and it would then silently rewrite a stored TargetGroup of CIF/ExceptCIF to
        // 'All' — after which save() posts cifFiles: [] and the message's CIF rows are deleted,
        // without the admin having touched anything.
        if (this.isPopulatingForm) return;
        if (newValue === true && this.formData.targetGroup !== 'All') {
          this.$swal.fire({
            icon: 'info',
            title: 'Information',
            text: 'When Default Flag is enabled, Target Group will be automatically set to "All"',
            confirmButtonText: 'OK',
          });
          this.formData.targetGroup = 'All';
          // this.formData.defaultFlag = false;
        }
      },
    },
    'formData.targetGroup': {
      handler(newValue, oldValue) {
        if (this.isPopulatingForm) return;   // same reason as the defaultFlag watcher above

        // A CIF List upload belongs only to that selection. Leaving CIF List clears it so returning
        // later cannot silently restore a stale audience. Except CIF and CIF List also clear each
        // other's uploads because the same file shape has opposite targeting meaning.
        if (shouldClearProactiveCifFiles(oldValue, newValue)) {
          if (this.uploadedFiles.length > 0) {
            this.$swal.fire({
              icon: 'info',
              title: 'CIF file cleared',
              text: getProactiveCifClearMessage(oldValue, newValue),
              confirmButtonText: 'OK',
            });
          }
          this.uploadedFiles = [];
          this.formData.cifFiles = [];
          if (this.$refs.fileInput) this.$refs.fileInput.value = '';
        }

        // Check if defaultFlag is true and targetGroup is changed to something other than 'All'
        if (this.formData.defaultFlag === true && newValue !== 'All') {
          this.$swal.fire({
            icon: 'warning',
            title: 'Cannot Change Target Group',
            text: 'When Default Flag is enabled, Target Group must remain as "All". Please disable Default Flag first if you want to change the Target Group.',
            confirmButtonText: 'OK',
          });
          // Revert back to 'All'
          this.$nextTick(() => {
            this.formData.targetGroup = 'All';
          });
        }
      },
    },
  },
  mounted() {
    this.GetDate();
    this.GetOptions();
  },
  beforeDestroy() {
    // Clean up debounce timer to prevent memory leaks
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
    }
    if (this.processingStatusClearTimer) {
      clearTimeout(this.processingStatusClearTimer);
    }
  },
  methods: {
    // Get data from API
    Get() {
      let self = this;
      self.dialog = true;
      self.loading = true;

      let { page, itemsPerPage, sortBy } = self.options;
      let sortField = null;
      let sortOrder = null;

      if (sortBy && sortBy.length > 0) {
        sortField = sortBy[0].key;
        sortOrder = sortBy[0].order === 'desc';
      }

      let request = {
        page: self.isSearch ? 1 : page,
        ItemsPerPage: itemsPerPage !== -1 ? itemsPerPage : self.totalTable,
        searchText: self.searchText,
        sortBy: sortField,
        sortDesc: sortOrder ?? false,
        statusFilter: self.getStatusFilter(),
        continueQuestionFilter: self.getContinueQuestionFilter(),
        targetGroupFilter: self.getTargetGroupFilter(),
        screenFilter: self.filters.screen,
        segmentFilter: self.filters.segment,
        wealthMembershipFilter: self.filters.wealthMembership,
        effectiveStartDateFilter: self.filters.effectiveStartDate || null,
        effectiveEndDateFilter: self.filters.effectiveEndDate || null,
      };

      self.$call.GetTableProactiveMessage(
        request,
        (dataResponse) => {
          if (dataResponse.status === 0) {
            self.searchTotalTable = dataResponse.data.totalCount;
            self.items = self.mapDataToItems(dataResponse.data.items);
            self.totalTable = self.isFirst ? dataResponse.data.totalCount : self.totalTable;
            self.syncBackgroundProcessingState(self.items);
          } else {
            self.items = [];
            self.searchTotalTable = 0;
            self.totalTable = 0;
            self.syncBackgroundProcessingState([]);
          }
          self.dialog = false;
          self.loading = false;
          self.isSearch = false;
          self.isFirst = false;
        },
        (errorResource) => {
          self.dialog = false;
          self.loading = false;
          self.isSearch = false;
          console.error('Error getting proactive messages:', errorResource);
        }
      );
    },

    // Map API data to display format
    mapDataToItems(apiData) {
      return apiData.map((item, index) => ({
        number: (this.options.page - 1) * this.options.itemsPerPage + index + 1,
        id: item.id,
        name: item.name,
        messageThai: item.messageThai,
        messageEnglish: item.messageEnglish,
        messageChinese: item.messageChinese,
        massage: item.messageThai || item.messageEnglish || item.messageChinese || 'No message',
        continueQuestion: item.hasContinueQuestion,
        screen: item.screen || [],
        targetGroup: item.targetGroup,
        cifCount: item.totalCifCount,
        segment: item.segment || [],
        wealthMembership: item.wealthMembership || [],
        priority: item.priority,
        effStartDate: this.formatDateTime(item.effectiveStartDate),
        effEndDate: this.formatDateTime(item.effectiveEndDate) || 'No end date',
        createDate: this.formatDate(item.createDate),
        status: item.enableFlag ? 'Active' : 'Inactive',
        cifProcessingStatus: item.cifProcessingStatus || 'None',
        // Risk 1 — states this message's Pending-in-State condition names that the active question
        // version no longer offers. Non-empty means the message can never fire.
        removedTaxConditionStates: item.removedTaxConditionStates || [],
        repeatFlag: item.repeatFlag,
        defaultFlag: item.defaultFlag,
        groupPromptTemplateID: item.id,
      }));
    },

    getCifProcessingStatusColor(status) {
      switch (status) {
        case 'Completed':
          return 'success';
        case 'Processing':
          return 'warning';
        case 'Error':
          return 'error';
        default:
          return 'grey';
      }
    },

    trackBackgroundProcessing(messageId) {
      if (!messageId) {
        return;
      }

      if (this.processingStatusClearTimer) {
        clearTimeout(this.processingStatusClearTimer);
        this.processingStatusClearTimer = null;
      }

      this.activeProcessingMessageId = messageId;
      this.activeProcessingSessionId = `proactive-message-${messageId}`;
      this.activeProcessingStatusList = [];
      this.activeProcessingCompleted = false;
      localStorage.setItem(PROACTIVE_MESSAGE_PROCESSING_STORAGE_KEY, messageId);
    },

    clearBackgroundProcessingTracking() {
      localStorage.removeItem(PROACTIVE_MESSAGE_PROCESSING_STORAGE_KEY);
      this.activeProcessingSessionId = null;
      this.activeProcessingMessageId = null;
      this.activeProcessingStatusList = [];
      this.activeProcessingCompleted = false;
    },

    syncBackgroundProcessingState(items) {
      const processingItems = (items || []).filter(item => item.cifProcessingStatus === 'Processing');
      const storedMessageId = localStorage.getItem(PROACTIVE_MESSAGE_PROCESSING_STORAGE_KEY);

      let activeItem = null;
      if (storedMessageId) {
        activeItem = processingItems.find(item => String(item.id) === String(storedMessageId)) || null;
      }

      if (!activeItem && processingItems.length > 0) {
        activeItem = processingItems[0];
      }

      if (activeItem) {
        if (this.processingStatusClearTimer) {
          clearTimeout(this.processingStatusClearTimer);
          this.processingStatusClearTimer = null;
        }

        this.activeProcessingMessageId = activeItem.id;
        this.activeProcessingSessionId = `proactive-message-${activeItem.id}`;
        this.activeProcessingCompleted = false;
        localStorage.setItem(PROACTIVE_MESSAGE_PROCESSING_STORAGE_KEY, activeItem.id);
        return;
      }

      const trackedItem = storedMessageId
        ? (items || []).find(item => String(item.id) === String(storedMessageId))
        : null;

      if (!trackedItem || trackedItem.cifProcessingStatus !== 'Processing') {
        this.clearBackgroundProcessingTracking();
      }
    },

    handleBackgroundStatusUpdate(payload) {
      this.activeProcessingStatusList = payload.statusList || [];
      this.activeProcessingCompleted = payload.isCompleted || false;

      if (payload.status === 1 || payload.status === 2) {
        this.Get();

        this.processingStatusClearTimer = setTimeout(() => {
          this.clearBackgroundProcessingTracking();
          this.processingStatusClearTimer = null;
        }, 5000);
      }
    },

    // Format date for display
    formatDate(dateString) {
      if (!dateString) return '';
      return moment(dateString).format('DD/MM/YYYY');
    },

    formatDateTime(dateString) {
      if (!dateString) return '';
      return moment(dateString).format('DD/MM/YYYY HH:mm');
    },

    // Debounced search method to prevent excessive API calls
    debouncedSearch(searchValue) {
      // Clear existing timer
      if (this.searchDebounceTimer) {
        clearTimeout(this.searchDebounceTimer);
      }

      // Set new timer with 400ms delay
      this.searchDebounceTimer = setTimeout(() => {
        this.Search();
      }, 400);
    },

    // Search method
    Search() {
      this.isSearch = true;
      this.options.page = 1;
      this.Get();
    },

    // Get options for dropdowns
    GetOptions() {
      let self = this;
      self.$call.GetProactiveMessageOptions(
        (dataResponse) => {
          if (dataResponse.status === 0) {
            self.options_data = dataResponse.data;
            self.screenOptions = dataResponse.data.screenOptions || [];
            self.segmentOptions = dataResponse.data.segmentOptions || [];
            self.wealthMembershipOptions = dataResponse.data.wealthMembershipOptions || [];
            self.taxYearOptions = dataResponse.data.taxYearOptions || [];
            self.taxStateOptions = dataResponse.data.taxStateOptions || [];
            self.taxOptionsLoaded = true;
            self.genieCharacterOptions = dataResponse.data.genieCharacterOptions || [];
            self.actionOptions = dataResponse.data.actionOptions || [];
          }
        },
        (errorResource) => {
          console.error('Error getting options:', errorResource);
        }
      );
    },

    // Filter helper methods
    getStatusFilter() {
      const hasSpecificSelection = this.filters.status.active || this.filters.status.inactive;
      const filters = [];
      if (!hasSpecificSelection && this.filters.status.all) filters.push('All');
      if (this.filters.status.active) filters.push('Active');
      if (this.filters.status.inactive) filters.push('Inactive');
      return filters.length ? filters : [];
    },

    getContinueQuestionFilter() {
      const hasSpecificSelection = this.filters.continueQuestion.yes || this.filters.continueQuestion.no;
      const filters = [];
      if (!hasSpecificSelection && this.filters.continueQuestion.all) filters.push('All');
      if (this.filters.continueQuestion.yes) filters.push('Yes');
      if (this.filters.continueQuestion.no) filters.push('No');
      return filters.length ? filters : [];
    },

    getTargetGroupFilter() {
      const hasSpecificSelection = this.filters.targetGroup.persona || this.filters.targetGroup.cif;
      const filters = [];
      if (!hasSpecificSelection && this.filters.targetGroup.all) filters.push('All');
      if (this.filters.targetGroup.persona) filters.push('Persona');
      if (this.filters.targetGroup.cif) filters.push('CIF');
      return filters.length ? filters : null;
    },

    GetDate() {
      let today1 = new Date();
      let dd = today1.getDate();
      let mm = today1.getMonth() + 1;
      let yyyy = today1.getFullYear();
      if (dd < 10) {
        dd = '0' + dd;
      }
      if (mm < 10) {
        mm = '0' + mm;
      }
      this.today = dd + '/' + mm + '/' + yyyy;
    },

    // Open Add Dialog
    openAddDialog() {
      this.isEditMode = false;
      this.editingItemId = null;
      this.addDialog = true;
      this.resetForm();
    },

    // Open Edit Dialog
    Edit(item) {
      let self = this;

      // 🔴 DO NOT OPEN THE DIALOG UNTIL THE DETAIL LOAD SUCCEEDS.
      //
      // This used to open immediately and populate later, with a failed load handled by a bare
      // console.error. The dialog then sat open showing whatever resetForm() had left — a BLANK
      // form: no continue questions, no CIF files, targetGroup back to 'All'. Change one field,
      // hit Save, and that emptiness is posted straight over the record: questions and quick
      // replies deleted, ProactiveMessageCif rows deleted, target group silently reset. The admin
      // sees nothing wrong, because a blank form and a genuinely empty message look identical.
      //
      // A detail load can fail for entirely ordinary reasons — an unapplied migration makes EF
      // select columns the database does not have, and every GET 500s — so this cannot be treated
      // as impossible. Failing loudly and NOT opening is the only safe response: no form, no save,
      // no loss.
      self.$call.GetProactiveMessageById(
        item.id,
        (dataResponse) => {
          if (dataResponse.status === 0 && dataResponse.data) {
            self.isEditMode = true;
            self.editingItemId = item.id;
            self.addDialog = true;
            self.populateFormWithItem(dataResponse.data);
          } else {
            self.showDetailLoadFailure(dataResponse && dataResponse.message);
          }
        },
        (errorResource) => {
          console.error('Error getting item details:', errorResource);
          self.showDetailLoadFailure(
            (errorResource && (errorResource.error || errorResource.message)) || null
          );
        }
      );
    },

    /**
     * Refuse to open the editor when its data could not be read, and say why.
     * Opening anyway would offer a blank form whose save destroys the record it failed to load.
     */
    showDetailLoadFailure(detail) {
      this.addDialog = false;
      this.isEditMode = false;
      this.editingItemId = null;
      this.$swal.fire({
        icon: 'error',
        title: 'Cannot open this proactive message',
        html:
          'Its details could not be loaded, so the form was not opened &mdash; editing now would '
          + 'overwrite the saved data with an empty form.'
          + (detail ? '<br><br><small>' + detail + '</small>' : ''),
        confirmButtonText: 'OK',
      });
    },

    // Populate form with existing item data
    populateFormWithItem(item) {
      this.isPopulatingForm = true;
      this.formData = {
        name: item.name || '',
        messageThai: item.messageThai || '',
        messageEnglish: item.messageEnglish || '',
        messageChinese: item.messageChinese || '',
        priority: item.priority ?? 1,
        // 🔴 Must carry the time, and must be exactly the shape <input type="datetime-local">
        // accepts. Formatting as 'YYYY-MM-DD' would silently truncate every stored time to
        // midnight the first time an existing message was opened and re-saved.
        effectiveStartDate: item.effectiveStartDate ? moment(item.effectiveStartDate).format('YYYY-MM-DDTHH:mm') : null,
        effectiveEndDate: item.effectiveEndDate ? moment(item.effectiveEndDate).format('YYYY-MM-DDTHH:mm') : null,
        targetGroup: item.targetGroup || 'All',
        screen: item.screen || [],
        segment: item.segment || [],
        wealthMembership: item.wealthMembership || [],
        cifFiles: [], // Will be populated below
        enableFlag: item.enableFlag !== undefined ? item.enableFlag : true,
        repeatFlag: item.repeatFlag || false,
        defaultFlag: item.defaultFlag || false,
        taxPlanningEnabled: item.taxPlanningEnabled || false,
        continueQuestions: (item.continueQuestions || []).map((q) => ({
          // Keep the server ID. The backend reconciles questions BY ID so a question — and the
          // quick replies hanging off it — survives a save; dropping the id here would make every
          // edit look like a delete-and-recreate to the API.
          id: q.id,
          questionThai: q.questionThai || '',
          questionEnglish: q.questionEnglish || '',
          questionChinese: q.questionChinese || '',
          quickReplyEnabled: q.quickReplyEnabled || false,
          quickReplies: (q.quickReplies || []).map((r) => ({
            textThai: r.textThai || '',
            textEnglish: r.textEnglish || '',
            textChinese: r.textChinese || '',
          })),
        })),

        actionTypeEnabled: !!item.actionType,
        actionType: item.actionType || 'ContinueQuestion',
        actionUrl: item.actionUrl || '',
        actionName: item.actionName || null,

        genieCharacterEnabled: item.genieCharacterEnabled || false,
        genieCharacter: item.genieCharacter || null,

        displayDays: item.displayDays || [],
        birthdayEnabled: item.birthdayEnabled || false,
        beforeBirthdayEnabled: item.beforeBirthdayEnabled || false,
        beforeBirthdayDays: item.beforeBirthdayDays ?? null,
        onBirthdayEnabled: item.onBirthdayEnabled || false,

        displayPeriod: item.displayPeriod || 'AllDay',
        displayStartTime: item.displayStartTime || null,
        displayEndTime: item.displayEndTime || null,

        taxConditionEnabled: item.taxConditionEnabled || false,
        // 🔴 No `|| 'Never'` fallback. The mode-to-condition backfill leaves these rows with
        //    TaxConditionEnabled = 1 and a NULL status, meaning "no condition on tax form state".
        //    Defaulting the blank to 'Never' would show a selection the admin never made and, on
        //    the next save, narrow the message to customers with no tax form at all. Left unset,
        //    validation makes them choose explicitly.
        taxConditionStatusType: item.taxConditionStatusType || null,
        taxConditionYears: item.taxConditionYears || [],
        taxConditionStates: item.taxConditionStates || [],
      };

      // §17/§18 — a stored reference that no longer resolves stays SELECTED and VISIBLE, marked
      // as unavailable. The backend tells us which ones those are; all we do is make sure the
      // select still has an item to render, so the value cannot silently vanish from the form and
      // then be saved back as empty.
      this.unresolvedActionName = item.actionNameResolved === false ? item.actionName : null;
      this.unresolvedGenieCharacter = item.genieCharacterResolved === false ? item.genieCharacter : null;
      this.removedTaxConditionStates = item.removedTaxConditionStates || [];
      
      // Handle multiple CIF files (new structure)
      if (item.cifFiles && Array.isArray(item.cifFiles)) {
        this.uploadedFiles = item.cifFiles.map(file => ({
          fileName: file.fileName,
          filePath: file.filePath,
          cifList: file.cifList || [],
          uploadDate: file.uploadDate ? new Date(file.uploadDate) : new Date(),
          fileSize: file.fileSize || 0,
          cifCount: file.cifCount
        }));
        this.formData.cifFiles = [...this.uploadedFiles];
      }
      // Handle legacy single file structure for backward compatibility
      else if (item.cifFileName && item.cifFilePath) {
        const legacyFile = {
          fileName: item.cifFileName,
          filePath: item.cifFilePath,
          cifList: item.cifList || [],
          uploadDate: new Date(),
          fileSize: 0 // Unknown for legacy data
        };
        this.uploadedFiles = [legacyFile];
        this.formData.cifFiles = [legacyFile];
      } else {
        this.uploadedFiles = [];
        this.formData.cifFiles = [];
      }

      // Lowered on the next tick, not here: Vue flushes watchers after the current tick, so
      // clearing it synchronously would let them run with the flag already down.
      this.$nextTick(() => { this.isPopulatingForm = false; });
    },

    // Close Dialog
    closeAddDialog() {
      this.addDialog = false;
      this.isEditMode = false;
      this.editingItemId = null;
      this.resetForm();
    },

    /** The Tax Planning condition's first broken rule as a message, or '' when it can be saved. */
    getTaxConditionError() {
      const f = this.formData;
      if (!f.taxConditionEnabled) return '';
      if (f.taxConditionStatusType !== 'NeverInYears' && f.taxConditionStatusType !== 'PendingInState') return '';

      const label = f.taxConditionStatusType === 'NeverInYears' ? 'Never Create in Specific Tax Year(s)' : 'Pending in Specific State';
      if ((f.taxConditionYears || []).length === 0) {
        return this.taxOptionsLoaded && this.taxYearOptions.length === 0
          ? `No tax year in Tax Config to select for "${label}". Add one under Tax Config first.`
          : `Select at least one Tax Year for "${label}".`;
      }
      if (this.removedTaxConditionYears.length) {
        return `Tax year ${this.removedTaxConditionYears.join(', ')} is not in Tax Config. Untick it and select a tax year from the list.`;
      }
      if (f.taxConditionStatusType === 'PendingInState') {
        if ((f.taxConditionStates || []).length === 0) {
          return `Select at least one Current State for "${label}".`;
        }
        const ticked = this.removedTaxConditionStates.filter((s) => f.taxConditionStates.includes(s));
        if (ticked.length) {
          return `${ticked.join(', ')} ${ticked.length === 1 ? 'is not a state group' : 'are not state groups'}. Untick it and select a state group instead.`;
        }
      }
      return '';
    },

    /**
     * §18 — returns the master list PLUS any currently-selected value that is no longer in it,
     * appended and flagged as removed.
     *
     * Without this a v-select silently renders nothing for an unknown value, the control looks
     * empty, and the next save writes that emptiness back — so deleting an option from a master
     * list would quietly strip it from every record referencing it. Keeping the value visible and
     * marked makes it the admin's decision instead of a side effect.
     */
    withRemovedOptions(options, selected, removed) {
      const list = [...(options || [])];
      const known = new Set(list.map((o) => (o.valueString ?? o.value ?? o)));

      const missing = [];
      const push = (value) => {
        if (value === null || value === undefined || value === '') return;
        if (known.has(value) || missing.includes(value)) return;
        missing.push(value);
      };

      if (Array.isArray(selected)) selected.forEach(push);
      else push(selected);
      (removed || []).forEach(push);

      missing.forEach((value) => {
        list.push({ name: `${value} (removed)`, valueString: value, removed: true });
      });

      return list;
    },

    /**
     * The action manager saved something — reload the option lists so a newly created action is
     * selectable immediately, without closing the form and losing everything typed so far.
     *
     * 🔴 GetOptions() replaces actionOptions wholesale, so the currently selected formData.actionName
     * is deliberately NOT touched: it is a name, and withRemovedOptions() keeps it rendered even if
     * it is momentarily absent from the refreshed list. Clearing it here would wipe the admin's
     * selection every time they opened the manager and cancelled.
     */
    onSuggestionActionsSaved() {
      this.GetOptions();
    },

    /** Day pills are a plain multi-select toggle. */
    toggleDisplayDay(day) {
      const days = this.formData.displayDays || [];
      const at = days.indexOf(day);
      if (at >= 0) days.splice(at, 1);
      else days.push(day);
      this.formData.displayDays = [...days];
    },

    isDisplayDaySelected(day) {
      return (this.formData.displayDays || []).includes(day);
    },

    applyDayPreset(preset) {
      const days = DAY_PRESETS[preset] || [];
      // Tapping the active preset again clears it, which means "every day" — the same thing an
      // empty selection means to the backend.
      this.formData.displayDays = this.isDayPresetActive(preset) ? [] : [...days];
    },

    isDayPresetActive(preset) {
      const wanted = [...(DAY_PRESETS[preset] || [])].sort().join(',');
      const current = [...(this.formData.displayDays || [])].sort().join(',');
      return wanted === current && wanted !== '';
    },

    /**
     * Birthday is a schedule of its own, not a day filter. Turning it on drops any weekday
     * selection so the backend's day axis sees only the birthday rule; the presets and pills
     * are disabled while it is on, so nothing can put the days back until it is turned off.
     */
    toggleBirthday() {
      const next = !this.formData.birthdayEnabled;
      this.formData.birthdayEnabled = next;
      if (next) this.formData.displayDays = [];
    },

    addQuickReply(question) {
      if (!question.quickReplies) question.quickReplies = [];
      question.quickReplies.push({ textThai: '', textEnglish: '', textChinese: '' });
    },

    removeQuickReply(question, index) {
      question.quickReplies.splice(index, 1);
    },

    onQuickReplyToggle(question) {
      // Ticking the box with no chips yet starts one, so the section is never an empty box the
      // admin has to guess at. Unticking keeps whatever was typed — the payload drops it, so
      // re-ticking by mistake does not cost the admin their text.
      if (question.quickReplyEnabled && (!question.quickReplies || question.quickReplies.length === 0)) {
        this.addQuickReply(question);
      }
    },

    // Reset Form
    resetForm() {
      this.formData = blankFormData();
      this.unresolvedActionName = null;
      this.unresolvedGenieCharacter = null;
      this.removedTaxConditionStates = [];
      this.uploadedFiles = []; // Reset uploaded files array
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = '';
      }
      if (this.$refs.addForm) {
        this.$refs.addForm.resetValidation();
      }
    },

    // Save/Update Proactive Message
    async saveProactiveMessage() {
      let self = this;
      const { valid } = await self.$refs.addForm.validate();
      if (valid) {
        // Validate defaultFlag and targetGroup
        if (self.formData.defaultFlag === true && self.formData.targetGroup !== 'All') {
          self.$swal.fire({
            icon: 'warning',
            title: 'Validation Error',
            text: 'When Default Flag is enabled, Target Group must be set to "All"',
            confirmButtonText: 'OK',
          });
          return;
        }

        // Default is the fallback message and only enabled rows are ever picked as one, so a
        // disabled default is a default nobody receives. The API rejects it (N-02); say so here,
        // before posting, instead of surfacing a bare 400. Reachable from a row saved before the rule.
        if (self.formData.defaultFlag === true && self.formData.enableFlag !== true) {
          self.$swal.fire({
            icon: 'warning',
            title: 'Validation Error',
            text: 'Enable cannot be off while Default Flag is on. Turn Enable on, or turn Default Flag off.',
            confirmButtonText: 'OK',
          });
          return;
        }

        // Persona with neither list chosen matches everyone, i.e. it is Target Group "All" wearing
        // the Persona label. At least one of the two has to be picked. (The API enforces this too.)
        if (self.formData.targetGroup === 'Persona'
            && (self.formData.segment || []).length === 0
            && (self.formData.wealthMembership || []).length === 0) {
          self.$swal.fire({
            icon: 'warning',
            title: 'Validation Error',
            text: 'When Target Group is Persona, select at least one Segment or Wealth Membership',
            confirmButtonText: 'OK',
          });
          return;
        }

        // The year/state pickers are checkbox lists and cannot carry a v-form rule, so the Tax Planning
        // condition is checked here — same rules as ValidateActionAndConditionsAsync on the API.
        const taxConditionError = self.getTaxConditionError();
        if (taxConditionError) {
          self.$swal.fire({
            icon: 'warning',
            title: 'Validation Error',
            text: taxConditionError,
            confirmButtonText: 'OK',
          });
          return;
        }

        self.dialog = true;

        // Prepare request with conditional field clearing based on targetGroup
        let request = {
          name: self.formData.name,
          messageThai: self.formData.messageThai,
          messageEnglish: self.formData.messageEnglish,
          messageChinese: self.formData.messageChinese,
          priority: self.formData.priority,
          effectiveStartDate: self.formData.effectiveStartDate,
          // Empty = no end date, the message never expires
          effectiveEndDate: self.formData.effectiveEndDate || null,
          targetGroup: self.formData.targetGroup,
          screen: self.formData.screen,
          segment: [], // Will be set conditionally
          wealthMembership: [], // Will be set conditionally
          cifFiles: [], // Will be set conditionally
          enableFlag: self.formData.enableFlag,
          repeatFlag: self.formData.repeatFlag,
          defaultFlag: self.formData.defaultFlag,
          // taxPlanningEnabled is NOT posted any more. The "For Tax Planning Mode" switch is gone
          // and the server derives the column from the Tax Planning condition checkbox
          // (ProactiveMessageBAL.ApplyActionAndConditions). Sending it here would let a stale form
          // value override that derivation.
          continueQuestions: (self.formData.continueQuestions || []).map((q) => ({
            id: q.id || '00000000-0000-0000-0000-000000000000',
            questionThai: q.questionThai,
            questionEnglish: q.questionEnglish,
            questionChinese: q.questionChinese,
            quickReplyEnabled: !!q.quickReplyEnabled,
            // Sent regardless of the toggle — soft persistence. Gating this on
            // quickReplyEnabled made unticking the box delete the chips server-side.
            quickReplies: q.quickReplies || [],
          })),

          // actionType null tells the API the section is switched off — that is the wire contract,
          // not an omission. The dependent fields are cleared here too so a URL typed and then
          // abandoned never reaches the server.
          // actionType null still means "section switched off" — that is the wire contract.
          // The DEPENDANTS are sent as typed rather than blanked: storage keeps them so switching
          // back restores the admin's work, and the server projects only the active one to the app.
          actionType: self.formData.actionTypeEnabled ? self.formData.actionType : null,
          actionUrl: self.formData.actionUrl || null,
          actionName: self.formData.actionName || null,

          // Everything below is sent AS ENTERED. Each enabling flag travels with its values, and
          // the runtime matcher consults the flag before the value — so a disabled setting is
          // inert without having to be destroyed.
          genieCharacterEnabled: self.formData.genieCharacterEnabled,
          genieCharacter: self.formData.genieCharacter || null,

          // Birthday and the weekday list are exclusive in the UI; a record edited from an older
          // version may still carry both, so the day list is dropped here too.
          displayDays: self.formData.birthdayEnabled ? [] : (self.formData.displayDays || []),
          birthdayEnabled: self.formData.birthdayEnabled,
          beforeBirthdayEnabled: self.formData.beforeBirthdayEnabled,
          beforeBirthdayDays: Number(self.formData.beforeBirthdayDays) || null,
          onBirthdayEnabled: self.formData.onBirthdayEnabled,

          displayPeriod: self.formData.displayPeriod || 'AllDay',
          displayStartTime: self.formData.displayStartTime || null,
          displayEndTime: self.formData.displayEndTime || null,

          taxConditionEnabled: self.formData.taxConditionEnabled,
          taxConditionStatusType: self.formData.taxConditionStatusType || null,
          taxConditionYears: self.formData.taxConditionYears || [],
          taxConditionStates: self.formData.taxConditionStates || [],
        };

        // 🔴 NO LONGER CLEARED BY TARGET GROUP. Posting segment/wealthMembership/cifFiles as []
        // because the current target group does not use them is what deleted an admin's uploaded
        // CIF list the moment they switched to All — the server treats an empty cifFiles as
        // "the files were removed". All three are now sent as held, so switching Target Group back
        // and forth is lossless. Which of them actually FILTERS a message is decided at runtime
        // from TargetGroup, not by erasing the other two here.
        request.segment = self.formData.segment || [];
        request.wealthMembership = self.formData.wealthMembership || [];
        request.cifFiles = self.formData.cifFiles || [];

        if (self.isEditMode) {
          // Update existing item
          self.$call.UpdateProactiveMessage(
            self.editingItemId,
            request,
            (dataResponse) => {
              if (dataResponse.status === 0) {
                if (dataResponse.data?.cifProcessingStatus === 'Processing') {
                  self.trackBackgroundProcessing(dataResponse.data?.id || self.editingItemId);
                  self.$swal.fire({
                    icon: 'info',
                    title: 'Saved',
                    text: 'Proactive message updated. CIF processing is running in background.',
                    timer: 2000,
                    showConfirmButton: false,
                  });
                } else {
                  self.$swal.fire({
                    icon: 'success',
                    title: 'Success',
                    text: 'Proactive message updated successfully',
                    timer: 1500,
                    showConfirmButton: false,
                  });
                }
                self.closeAddDialog();
                self.Get();
              } else {
                self.$swal.fire({
                  icon: 'error',
                  title: 'Error',
                  text: dataResponse.message || 'Failed to update proactive message',
                });
              }
              self.dialog = false;
            },
            (errorResource) => {
              self.dialog = false;
              console.error('Error updating proactive message:', errorResource);
              self.$swal.fire({
                icon: 'error',
                title: 'Error',
                // A 400 carries the rule that failed in `error` (ProactiveMessageController) — show it.
                text: errorResource?.response?.data?.error || 'Failed to update proactive message',
              });
            }
          );
        } else {
          // Create new item
          self.$call.CreateProactiveMessage(
            request,
            (dataResponse) => {
              if (dataResponse.status === 0) {
                if (dataResponse.data?.cifProcessingStatus === 'Processing') {
                  self.trackBackgroundProcessing(dataResponse.data?.id);
                  self.$swal.fire({
                    icon: 'info',
                    title: 'Saved',
                    text: 'Proactive message created. CIF processing is running in background.',
                    timer: 2000,
                    showConfirmButton: false,
                  });
                } else {
                  self.$swal.fire({
                    icon: 'success',
                    title: 'Success',
                    text: 'Proactive message created successfully',
                    timer: 1500,
                    showConfirmButton: false,
                  });
                }
                self.closeAddDialog();
                self.Get();
              } else {
                self.$swal.fire({
                  icon: 'error',
                  title: 'Error',
                  text: dataResponse.message || 'Failed to create proactive message',
                });
              }
              self.dialog = false;
            },
            (errorResource) => {
              self.dialog = false;
              console.error('Error creating proactive message:', errorResource);
              self.$swal.fire({
                icon: 'error',
                title: 'Error',
                text: errorResource?.response?.data?.error || 'Failed to create proactive message',
              });
            }
          );
        }
      }
    },

    // Delete method
    DeleteGroup(id) {
      let self = this;
      self.$swal
        .fire({
          title: 'Confirm Deletion',
          text: 'Are you sure you want to delete this proactive message?',
          icon: 'warning',
          showCancelButton: true,
          confirmButtonColor: '#3085d6',
          cancelButtonColor: '#d33',
          confirmButtonText: 'Yes, delete it!',
          cancelButtonText: 'Cancel',
        })
        .then((result) => {
          if (result.isConfirmed) {
            self.dialog = true;
            self.$call.DeleteProactiveMessage(
              id,
              (dataResponse) => {
                if (dataResponse.status === 0) {
                  self.$swal.fire({
                    icon: 'success',
                    title: 'Deleted!',
                    text: 'Proactive message has been deleted.',
                    timer: 1500,
                    showConfirmButton: false,
                  });
                  self.Get();
                } else {
                  self.$swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: dataResponse.message || 'Failed to delete proactive message',
                  });
                }
                self.dialog = false;
              },
              (errorResource) => {
                self.dialog = false;
                console.error('Error deleting proactive message:', errorResource);
                self.$swal.fire({
                  icon: 'error',
                  title: 'Error',
                  text: 'Failed to delete proactive message',
                });
              }
            );
          }
        });
    },

    // File upload methods
    triggerFileUpload() {
      this.$refs.fileInput.click();
    },

    downloadTemplate() {
      // Create a simple CSV template with CIF header
      const csvContent = 'cif\n12345678901\n23456789012\n34567890123';
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      
      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', 'cif_template.csv');
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        
        this.$swal.fire({
          icon: 'success',
          title: 'Template Downloaded',
          text: 'CIF template file has been downloaded successfully!',
          timer: 1500,
          showConfirmButton: false,
        });
      }
    },

    handleFileUpload(event) {
      const file = event.target.files[0];
      if (file) {
        const allowedTypes = ['text/csv', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.ms-excel'];
        if (!allowedTypes.includes(file.type)) {
          this.$swal.fire({
            icon: 'warning',
            title: 'Invalid File Type',
            text: 'Please upload only CSV or Excel files',
          });
          return;
        }

        const maxSize = 5 * 1024 * 1024; // 5MB
        if (file.size > maxSize) {
          this.$swal.fire({
            icon: 'warning',
            title: 'File Too Large',
            text: 'File size should not exceed 5MB',
          });
          return;
        }

        // Check if file with same name already exists
        const existingFile = this.uploadedFiles.find(f => f.fileName === file.name);
        if (existingFile) {
          this.$swal.fire({
            icon: 'warning',
            title: 'Duplicate File',
            text: 'A file with this name has already been uploaded. Please rename the file or remove the existing one.',
          });
          return;
        }

        // Upload file to server using new API
        const formData = new FormData();
        formData.append('file', file);

        this.$call.UploadCifFile(
          formData,
          (dataResponse) => {
            if (dataResponse.status === 0) {
              // Add new file to the uploaded files array
              const newCifFile = {
                fileName: dataResponse.data.fileName || file.name,
                filePath: dataResponse.data.fileUrl,
                cifList: dataResponse.data.cifList || [],
                uploadDate: new Date(),
                fileSize: file.size,
                cifCount: dataResponse.data.cifCount || 0
              };
              
              this.uploadedFiles.push(newCifFile);
              
              // Update formData.cifFiles for backend compatibility
              this.formData.cifFiles = [...this.uploadedFiles];
              
              this.$swal.fire({
                icon: 'success',
                title: 'Success',
                text: `File uploaded successfully. Found ${dataResponse.data.cifCount || 0} CIF codes.`,
                timer: 2000,
                showConfirmButton: false,
              });
              
              // Reset file input
              this.$refs.fileInput.value = '';
            } else {
              this.$swal.fire({
                icon: 'error',
                title: 'Error',
                text: dataResponse.message || 'Failed to upload file',
              });
            }
          },
          (errorResource) => {
            console.error('Error uploading file:', errorResource);
            this.$swal.fire({
              icon: 'error',
              title: 'Error',
              text: errorResource.response?.data?.error || 'Failed to upload file',
            });
          }
        );
      }
    },

    // Remove specific CIF file from the list
    removeCifFile(index) {
      this.$swal.fire({
        title: 'Confirm Delete',
        text: `Are you sure you want to delete "${this.uploadedFiles[index].fileName}"?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Yes, delete it!'
      }).then((result) => {
        if (result.isConfirmed) {
          this.uploadedFiles.splice(index, 1);
          this.formData.cifFiles = [...this.uploadedFiles];
          
          this.$swal.fire({
            icon: 'success',
            title: 'Deleted!',
            text: 'File has been deleted.',
            timer: 1500,
            showConfirmButton: false,
          });
        }
      });
    },

    // Download specific CIF file
    downloadCifFile(file) {
      if (file && file.filePath) {
        // Create a temporary link to download the file
        const link = document.createElement('a');
        link.href = file.filePath;
        link.download = file.fileName;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        this.$swal.fire({
          icon: 'success',
          title: 'Downloading',
          text: 'File download started successfully!',
          timer: 1500,
          showConfirmButton: false,
        });
      } else {
        this.$swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'No file available for download',
        });
      }
    },

    // Clear all uploaded files
    clearAllFiles() {
      this.$swal.fire({
        title: 'Confirm Clear All',
        text: `Are you sure you want to delete all ${this.uploadedFiles.length} files?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Yes, clear all!'
      }).then((result) => {
        if (result.isConfirmed) {
          this.removeAllUploadedFiles();
          
          this.$swal.fire({
            icon: 'success',
            title: 'Cleared!',
            text: 'All files have been deleted.',
            timer: 1500,
            showConfirmButton: false,
          });
        }
      });
    },

    // Clear all uploaded files
    removeAllUploadedFiles() {
      this.uploadedFiles = [];
      this.formData.cifFiles = [];
      this.$refs.fileInput.value = '';
    },

    // Helper method to format file size
    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    },

    // Helper method to format upload date
    formatUploadDate(date) {
      if (!date) return '';
      const d = new Date(date);
      return d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'});
    },

    // #928 ตัดรายการชิปในตารางให้เหลือ max อัน ที่เหลือแสดงเป็น "+N"
    visibleChips(list, max = 2) {
      const items = Array.isArray(list) ? list : [];
      return { shown: items.slice(0, max), hiddenCount: Math.max(items.length - max, 0) };
    },

    // Helper method to get Screen name from WealthFlag
    getScreenName(screenValue) {
      if (!screenValue) return '';
      const option = this.screenOptions.find(opt => opt.valueString === screenValue);
      return option ? option.name : screenValue;
    },

    // Helper method to get Membership name from WealthFlag
    getWealthMembershipName(wealthFlag) {
      if (!wealthFlag) return '';
      const option = this.wealthMembershipOptions.find(opt => opt.valueString === wealthFlag);
      return option ? option.name : wealthFlag;
    },

    // Helper method to get multiple Membership names from WealthFlags array
    getWealthMembershipNames(wealthFlags) {
      if (!wealthFlags || !Array.isArray(wealthFlags)) return [];
      return wealthFlags.map(flag => this.getWealthMembershipName(flag));
    },

    // Continue Question methods
    addContinueQuestion() {
      const newQuestion = {
        questionThai: '',
        questionEnglish: '',
        questionChinese: '',
      };
      this.formData.continueQuestions.push(newQuestion);
    },

    removeContinueQuestion(index) {
      this.formData.continueQuestions.splice(index, 1);
    },

    // Filter methods
    toggleFilter() {
      this.filterDrawer = !this.filterDrawer;
    },

    toggleAllStatus() {
      if (this.filters.status.all) {
        this.filters.status.active = false;
        this.filters.status.inactive = false;
      }
    },

    handleSpecificStatusChange() {
      if (this.filters.status.active || this.filters.status.inactive) {
        this.filters.status.all = false;
      }
    },

    toggleAllContinueQuestion() {
      if (this.filters.continueQuestion.all) {
        this.filters.continueQuestion.yes = false;
        this.filters.continueQuestion.no = false;
      }
    },

    handleSpecificContinueQuestionChange() {
      if (this.filters.continueQuestion.yes || this.filters.continueQuestion.no) {
        this.filters.continueQuestion.all = false;
      }
    },

    toggleAllTargetGroup() {
      if (this.filters.targetGroup.all) {
        this.filters.targetGroup.persona = false;
        this.filters.targetGroup.cif = false;
      }
    },

    handleSpecificTargetGroupChange() {
      if (this.filters.targetGroup.persona || this.filters.targetGroup.cif) {
        this.filters.targetGroup.all = false;
      }
    },

    clearFilters() {
      this.filters = {
        status: {
          all: true,
          active: false,
          inactive: false,
        },
        continueQuestion: {
          all: true,
          yes: false,
          no: false,
        },
        targetGroup: {
          all: true,
          persona: false,
          cif: false,
        },
        screen: [],
        segment: [],
        wealthMembership: [],
        effectiveStartDate: null,
        effectiveEndDate: null,
      };
      this.Get(); // Refresh data after clearing filters
    },

    applyFilters() {
      this.filterDrawer = false;
      this.Get(); // Refresh data with new filters
    },

    // Export functionality
    exportToExcel() {
      let self = this;
      self.exportLoading = true;

      // Prepare the same request as used for table data
      let request = {
        searchText: self.searchText,
        statusFilter: self.getStatusFilter(),
        continueQuestionFilter: self.getContinueQuestionFilter(),
        targetGroupFilter: self.getTargetGroupFilter(),
        screenFilter: self.filters.screen,
        segmentFilter: self.filters.segment,
        wealthMembershipFilter: self.filters.wealthMembership,
        effectiveStartDateFilter: self.filters.effectiveStartDate || null,
        effectiveEndDateFilter: self.filters.effectiveEndDate || null,
        // For export, we want all data
        page: 1,
        perPage: 999999,
      };

      self.$call.ExportProactiveMessage(
        request,
        (data) => {
          // Create blob and download
          const blob = new Blob([data], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          });

          // Create download link
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;

          // Generate filename with current date and time
          const now = new Date();
          const dateStr = now.toISOString().slice(0, 10).replace(/-/g, '');
          const timeStr = now.toTimeString().slice(0, 8).replace(/:/g, '');
          link.download = `ProactiveMessages_${dateStr}_${timeStr}.xlsx`;

          // Trigger download
          document.body.appendChild(link);
          link.click();

          // Cleanup
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);

          self.exportLoading = false;

          // Show success message
          self.$swal.fire({
            icon: 'success',
            title: 'Export Successful',
            text: 'Proactive messages have been exported to Excel successfully!',
            showConfirmButton: false,
            timer: 2000,
          });
        },
        (error) => {
          self.exportLoading = false;
          console.error('Export error:', error);

          self.$swal.fire({
            icon: 'error',
            title: 'Export Failed',
            text: 'Failed to export proactive messages. Please try again.',
            showConfirmButton: true,
          });
        }
      );
    },
  },
};
</script>

<style scoped>
.card2 {
  border-radius: 10px !important;
  margin-bottom: 2rem;
  padding-top: 1rem;
}

.processing-status-card {
  border-radius: 16px;
  border: 1px solid #e6dcc8;
  background: linear-gradient(135deg, #fffaf1 0%, #f8f0df 100%);
}

.processing-status-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.processing-status-title {
  font-size: 16px;
  font-weight: 700;
  color: #46311a;
}

.processing-status-subtitle {
  font-size: 13px;
  color: #7b6245;
}

.btn-layout {
  padding: 14px 24px;

  width: 171px;
  height: 57px;

  background: linear-gradient(90deg, #504897 0%, #4b2667 100%);
  border-radius: 16px;
}
.btn-save {
  color: #fff !important;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 8px 24px;

  width: 120px;
  height: 45px;

  background: linear-gradient(90deg, #504897 0%, #4b2667 100%);
  border-radius: 16px;

  /* Inside auto layout */
  flex: none;
  order: 1;
  flex-grow: 0;
}
.btn-close {
  box-sizing: border-box;

  /* Auto layout */
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 8px 16px;
  gap: 10px;

  width: 135px;
  height: 45px;

  border: 1px solid #b4b4b4;
  border-radius: 18px;

  /* Inside auto layout */
  flex: none;
  order: 0;
  flex-grow: 0;
}
.btn-question {
  color: #fff !important;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 8px 24px;

  width: 153px;
  height: 45px;

  background: linear-gradient(90deg, #504897 0%, #4b2667 100%);
  border-radius: 16px;
}
.btn-filter {
  padding: 6px 8px;
  gap: 8px;

  width: 56px;
  height: 56px;

  background: #4b2869;
  border-radius: 1000px;
}
.btn-export {
  padding: 10px 32px;
  gap: 8px;

  width: 152px;
  height: 57px;

  background: rgba(24, 92, 55, 0.1);
  border: 1px solid #185c37;
  border-radius: 16px;
}
.search-field {
  border-radius: 30px !important;
  z-index: 9999 !important;
}
.filter-field {
  border-radius: 16px !important;
}

.custom-rounded :deep(.v-field) {
  border-radius: 20px !important; /* กำหนดค่าที่ต้องการ */
}

/* Error styling for v-text-field - red background and border */
.custom-rounded.v-input--error :deep(.v-field) {
  background-color: #ffebee !important;
  border-color: #f44336 !important;
}

.custom-rounded.v-input--error :deep(.v-field__outline) {
  --v-field-border-opacity: 1;
}

.custom-rounded.v-input--error :deep(.v-field__outline__start),
.custom-rounded.v-input--error :deep(.v-field__outline__end),
.custom-rounded.v-input--error :deep(.v-field__outline__notch::before),
.custom-rounded.v-input--error :deep(.v-field__outline__notch::after) {
  border-color: #f44336 !important;
}

.custom-rounded :deep(.v-field__outline) {
  border-radius: 20px !important;
}

.custom-rounded :deep(.v-field__field) {
  border-radius: 20px !important;
}

.filter-rounded :deep(.v-field) {
  border-radius: 16px !important; /* กำหนดค่าที่ต้องการ */
}

.filter-rounded :deep(.v-field__outline) {
  border-radius: 16px !important;
}

.filter-rounded :deep(.v-field__field) {
  border-radius: 16px !important;
}

.search-field .v-input__control .v-input__slot {
  min-height: 48px !important;
  background: #f5f5f5 !important;
  box-shadow: none !important;
  border: 1px solid #e0e0e0 !important;
}

.search-field .v-input__prepend-inner {
  margin-right: 8px;
  margin-left: 4px;
}

.search-field .v-icon {
  color: #666;
}

.filter-field .v-input__control .v-input__slot {
  min-height: 48px !important;
  background: #f5f5f5 !important;
  box-shadow: none !important;
  border: 1px solid #e0e0e0 !important;
}

.filter-field .v-input__prepend-inner {
  margin-right: 8px;
  margin-left: 4px;
}

.filter-field .v-icon {
  color: #666;
}

:deep(.v-table__wrapper > table > thead) {
  position: sticky !important;
  top: 0 !important;
  z-index: 10 !important; /* ต้อง > td.sticky-* (z-index: 3) เพื่อให้ thead stacking context อยู่เหนือ body sticky cells */
}

:deep(.v-table__wrapper thead tr) {
  background-color: #f5f5f5 !important;
}

:deep(.v-table__wrapper thead th) {
  position: sticky !important;
  top: 0 !important;
  z-index: 4 !important;
  background-color: #f5f5f5 !important;
  color: #333333;
  font-size: 14px;
  font-weight: 600;
  height: 48px;
}

/* ถ้ายังไม่ได้ผลลองใช้ selector ที่จำเพาะมากขึ้น */
:deep(.v-data-table-server .v-table thead th) {
  position: sticky !important;
  top: 0 !important;
  z-index: 4 !important;
  background-color: #f5f5f5 !important;
  color: #333333;
  font-size: 14px;
  font-weight: 600;
  height: 48px;
}

/* ถ้าต้องการใช้กับทุก element ในหัวตาราง */
:deep(.v-data-table-server .v-table thead tr th) {
  background-color: #f5f5f5 !important;
}

/* เพิ่ม CSS สำหรับตาราง */
:deep(.v-data-table) {
  border: 1px solid #e1e1e1;
  border-radius: 24px;
  overflow: clip; /* clip ไม่สร้าง scroll container จึงไม่ทำลาย position:sticky */
}

:deep(.v-table) {
  border-radius: 24px;
  overflow: clip; /* clip ไม่สร้าง scroll container จึงไม่ทำลาย position:sticky */
}
/* horizontal scroll ให้ .v-table__wrapper จัดการเท่านั้น — ห้ามตั้ง overflow-x: auto ที่ .v-data-table เพราะจะสร้าง scroll container ซ้อนที่ทำลาย sticky */
.table-container {
  overflow-x: auto;
  max-width: 100%;
  position: relative;
}

/* ===== Sticky columns: แยก td (body) vs th (header) ===== */

/* Body cells - sticky แนวนอนเท่านั้น */
td.sticky-action {
  position: sticky !important;
  right: 0 !important;
  background-color: #fff !important;
  z-index: 3 !important;
}

td.sticky-repeat {
  position: sticky !important;
  right: 12% !important;
  background-color: #fff !important;
  z-index: 3 !important;
}

td.sticky-status {
  position: sticky !important;
  right: 17% !important;
  background-color: #fff !important;
  z-index: 3 !important;
}

/* Header cells - sticky ทั้งแนวนอนและแนวตั้ง, z-index สูงกว่า body */
th.sticky-action {
  position: sticky !important;
  right: 0 !important;
  top: 0 !important;
  background-color: #f5f5f5 !important;
  z-index: 5 !important;
}

th.sticky-repeat {
  position: sticky !important;
  right: 12% !important;
  top: 0 !important;
  background-color: #f5f5f5 !important;
  z-index: 5 !important;
}

th.sticky-status {
  position: sticky !important;
  right: 17% !important;
  top: 0 !important;
  background-color: #f5f5f5 !important;
  z-index: 5 !important;
}

/* ตาราง */
:deep(.my-data-table .v-table__wrapper) {
  overflow-x: auto !important;
}

:deep(.my-data-table table) {
  min-width: 2000px;
}

/* เพิ่ม CSS สำหรับ segment chips */
.data-container {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  justify-content: center;
  align-items: center;
  max-width: 200px;
  margin: 0 auto;
}

.data-chip {
  font-size: 12px !important;

  color: #666 !important;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 4px 16px;
  gap: 8px;

  width: 136px;
  height: 32px;

  background-color: #f2f2f2 !important;
  border-radius: 100px;
}

/* ปรับแต่งสีของ v-chip สำหรับ segment */
:deep(.data-chip .v-chip__content) {
  font-size: 11px;
  font-weight: 500;
  padding: 0 8px;
}

/* หากต้องการสีเทาเข้มขึ้น */
:deep(.v-chip.data-chip) {
  background-color: #f2f2f2 !important;
  color: black !important;
}

/* #928 ชิป "+N" แคบพอให้อยู่แถวเดียวกับชิปที่ 2 */
.data-chip-more {
  font-size: 11px !important;
  font-weight: 600;
  height: 32px !important;
  padding: 0 10px !important;
  border-radius: 100px;
  background-color: #e6e6e6 !important;
  color: #333 !important;
  cursor: pointer;
}

/* #928 รายชื่อ screen ทั้งหมดในเมนูของชิป "+N" (v-menu teleport ไป overlay จึงไม่โดนคอลัมน์ sticky บัง) */
.chip-list-card {
  width: 480px;
  max-width: 90vw;
  max-height: 320px;
  overflow-y: auto;
  padding: 8px 0;
}

/* แถวละ 3 ชื่อ */
.chip-list-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
  padding: 10px 12px 4px;
}

.chip-list-title {
  font-size: 12px;
  font-weight: 600;
  color: #666;
  padding: 4px 16px 8px;
  border-bottom: 1px solid #eee;
}

/* แต่ละชื่อเป็น bubble โทนเดียวกับชิปในตาราง */
.chip-list-item {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 32px;
  padding: 6px 12px;
  border-radius: 16px;
  background-color: #f2f2f2;
  color: #333;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.3;
  text-align: center;
  white-space: normal;
  word-break: break-word;
}

.icon-active {
  color: #4caf50 !important; /* สีเขียว */
  opacity: 1;
}

.icon-inactive {
  color: #e0e0e0 !important; /* สีเทาอ่อน */
  opacity: 0.5;
}

/* หรือใช้ตัวนี้แทนหากต้องการควบคุมสีมากขึ้น */
:deep(.v-icon.icon-active) {
  color: #4caf50 !important;
}

:deep(.v-icon.icon-inactive) {
  color: #bdbdbd !important;
}

/* Filter Drawer Styles */
.filter-drawer {
  z-index: 9000 !important;
}

.filter-card {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.filter-header {
  background-color: #fff;
  border-bottom: 1px solid #e9ecef;
  padding: 16px 20px;
  font-weight: 600;
  font-size: 16px;
}

.filter-content {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
}

.filter-section {
  margin-bottom: 24px;
}

.filter-section-title {
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin-bottom: 12px;
}

.filter-checkbox {
  margin-bottom: 4px;
}

:deep(.filter-checkbox .v-selection-control) {
  min-height: 32px;
}

:deep(.filter-checkbox .v-label) {
  font-size: 14px;
  color: #555;
}

.filter-select {
  margin-top: 8px;
}

.filter-date {
  margin-top: 8px;
}

.filter-actions {
  border-top: 1px solid #e9ecef;
  background-color: #f8f9fa;
}

.add-proactive-dialog {
  border-radius: 16px !important;
}

.dialog-header {
  padding: 20px 16px !important;
}

.dialog-title {
  font-size: 1.25rem !important;
  color: #1f1f1f;
}
.dialog-subtitle {
  color: #898989;
}
.icon-background {
  padding: 8px;
  gap: 10px;

  width: 46px;
  height: 46px;

  background: #dee7f3;
  backdrop-filter: blur(2px);
  /* Note: backdrop-filter has minimal browser support */
  border-radius: 100px;
  box-shadow: none;
}
.dialog-content {
  padding: 24px;
  max-height: 70vh;
  overflow-y: auto;
}

.form-section {
  margin-bottom: 24px;
}
.form-section-bg {
  padding: 16px;
  gap: 24px;
  height: 389px;

  background: #eeeff3;
  border-radius: 16px;

  /* Inside auto layout */
  flex: none;
  order: 0;
  align-self: stretch;
  flex-grow: 0;
}

.section-title {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin-bottom: 12px;
}

/* Multilingual message display styles */
.message-multilang {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 8px 0;
}

.message-box {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 8px;
  border-left: 3px solid;
  background-color: #f8f9fa;
  transition: all 0.2s ease;
}

.message-box:hover {
  background-color: #f0f1f3;
}

.message-box-thai {
  border-left-color: #1e88e5;
  background-color: #e3f2fd;
}

.message-box-thai:hover {
  background-color: #bbdefb;
}

.message-box-english {
  border-left-color: #43a047;
  background-color: #e8f5e9;
}

.message-box-english:hover {
  background-color: #c8e6c9;
}

.message-box-chinese {
  border-left-color: #e53935;
  background-color: #ffebee;
}

.message-box-chinese:hover {
  background-color: #ffcdd2;
}

.message-lang-header {
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 45px;
  flex-shrink: 0;
}

.lang-label {
  font-size: 10px;
  font-weight: 700;
  color: #666;
  text-transform: uppercase;
}

.lang-flag {
  flex-shrink: 0;
}

.message-text {
  font-size: 13px;
  color: #333;
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 250px;
}

.message-line {
  display: flex;
  align-items: center;
  gap: 8px;
}

.lang-flag {
  flex-shrink: 0;
}

.message-input-group {
  margin-bottom: 5px;
}

.language-label {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-bottom: 8px;
  font-size: 16px;
  font-weight: 500;
}

.message-textarea {
  margin-bottom: 8px;
}

.continue-btn {
  height: 40px;
  border-radius: 8px;
  text-transform: none;
  font-weight: 500;
}

.dialog-actions {
  border-top: 1px solid #e9ecef;
  background-color: #f8f9fa;
  border-radius: 0 0 16px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 24px;
}

.toggle-switches-section {
  display: flex;
  align-items: center;
}

.action-buttons {
  display: flex;
  align-items: center;
  gap: 8px;
}

.action-btn {
  height: 40px;
  min-width: 80px;
  border-radius: 8px;
  text-transform: none;
  font-weight: 500;
}

/* ปรับแต่ง Switch ให้เล็กลง */
:deep(.toggle-switches-section .v-switch) {
  flex: none;
}

:deep(.toggle-switches-section .v-switch .v-selection-control) {
  min-height: 32px;
}

:deep(.toggle-switches-section .v-switch .v-label) {
  font-size: 14px;
  margin-left: 8px;
}

/* Dialog responsive */
@media (max-width: 768px) {
  .dialog-actions {
    flex-direction: column;
    gap: 16px;
    align-items: stretch;
  }

  .toggle-switches-section {
    justify-content: center;
  }

  .action-buttons {
    justify-content: center;
    width: 100%;
  }

  .action-btn {
    flex: 1;
    max-width: 120px;
  }
  .add-proactive-dialog {
    margin: 16px;
  }

  .dialog-content {
    padding: 16px;
  }
}

.header-icon-container {
  width: 48px;
  height: 48px;
  margin-right: 12px;
}

.dialog-header-add {
  background: #eeeff3;
}
.slide-fade-enter-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.slide-fade-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.slide-fade-enter-from {
  opacity: 0;
  transform: translateY(-20px);
  max-height: 0;
}

.slide-fade-leave-to {
  opacity: 0;
  transform: translateY(-20px);
  max-height: 0;
}

.slide-fade-enter-to,
.slide-fade-leave-from {
  opacity: 1;
  transform: translateY(0);
  max-height: 200px;
}

.upload-btn {
  /* Auto layout */
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 10px 32px;
  gap: 8px;

  width: 167px;
  height: 40px;

  background: rgba(24, 92, 55, 0.1);
  border: 1px solid #185c37;
  border-radius: 8px;

  /* Inside auto layout */
  flex: none;
  order: 3;
  flex-grow: 0;
}
.download-btn {
  /* Auto layout */
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 8px 24px;

  width: 236px;
  height: 45px;

  background: linear-gradient(90deg, #504897 0%, #4b2667 100%);
  border-radius: 16px;

  color: #fff !important;

  /* Inside auto layout */
  flex: none;
  order: 3;
  flex-grow: 0;
}

.uploaded-file-info {
  margin-top: 12px;
}

/* Fade transition for upload button and file info */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.custom-switch :deep(.v-switch) {
  flex: none;
}

.custom-switch :deep(.v-selection-control) {
  min-height: 32px;
  display: flex;
  align-items: center;
}

.custom-switch :deep(.v-selection-control__wrapper) {
  height: 24px;
  width: 44px;
  margin-right: 1px;
}

.custom-switch :deep(.v-switch__track) {
  height: 24px;
  width: 44px;
  border-radius: 12px;
  background-color: #e0e0e0 !important;
  border: 1px solid #d0d0d0;
  transition: all 0.3s ease;
}

.custom-switch :deep(.v-switch__thumb) {
  height: 20px;
  width: 20px;
  border-radius: 50%;
  background-color: #fff;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  transition: all 0.3s ease;
  transform: translateX(2px);
}

/* Active state */
.custom-switch :deep(.v-selection-control--dirty .v-switch__track) {
  background-color: #6366f1 !important;
  border-color: #6366f1;
}

.custom-switch :deep(.v-selection-control--dirty .v-switch__thumb) {
  transform: translateX(1px);
  background-color: #fff;
}

/* Label styling */
.custom-switch :deep(.v-label) {
  font-size: 14px;
  font-weight: 500;
  color: #333;
  margin-left: 1px;
}

/* Hover effects */
.custom-switch :deep(.v-switch__track):hover {
  opacity: 0.8;
}

.custom-switch :deep(.v-switch__thumb):hover {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

/* Focus styles */
.custom-switch :deep(.v-selection-control--focus-visible .v-switch__track) {
  box-shadow: 0 0 0 2px rgba(99, 102, 241, 0.2);
}

/* Disabled state */
.custom-switch :deep(.v-selection-control--disabled .v-switch__track) {
  /* background-color: #f5f5f5 !important;
  border-color: #e0e0e0 !important; */
  opacity: 0.8;
}

.custom-switch :deep(.v-selection-control--disabled .v-switch__thumb) {
  background-color: #ffffff !important;
  box-shadow: none;
}

.custom-switch :deep(.v-selection-control--disabled .v-label) {
  color: #000000 !important;
}

.continue-questions-list {
  margin-bottom: 16px;
}

.continue-question-item {
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 16px;
  position: relative;
}

.question-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.question-title {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin: 0;
}

.continue-question-item .message-input-group {
  margin-bottom: 12px;
}

.continue-question-item .language-label {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-bottom: 8px;
  font-size: 14px;
  font-weight: 500;
  color: #555;
}

/* Animation for adding/removing questions */
.continue-question-item {
  animation: slideIn 0.3s ease-out;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* File Table Styles */
.file-table-card {
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.file-table {
  border-radius: 0;
}

.file-table th {
  background-color: #f8f9fa;
  font-weight: 600;
  font-size: 14px;
  color: #333;
  padding: 12px 16px;
  border-bottom: 2px solid #e9ecef;
}

.file-table td {
  padding: 12px 16px;
  vertical-align: middle;
}

.file-name {
  font-weight: 500;
  font-size: 14px;
  color: #333;
}

.action-buttons-group {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.action-buttons-group .v-btn {
  height: 32px;
  min-width: 80px;
  font-size: 12px;
  font-weight: 500;
  border-radius: 6px;
}

.action-buttons-group .v-btn--outlined {
  border-width: 1px;
}

.modern-table :deep(.v-table__wrapper) {
  max-height: 600px;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: rgba(25, 118, 210, 0.3) rgba(0, 0, 0, 0.05);
}

.modern-table :deep(.v-table__wrapper > table > thead) {
  position: sticky !important;
  top: 0 !important;
  z-index: 10 !important;
}

.modern-table :deep(.v-table__wrapper thead th) {
  position: sticky;
  top: 0;
  z-index: 4;
  background-color: #f5f5f5;
}

.modern-table :deep(.v-table__wrapper thead th.sticky-action),
.modern-table :deep(.v-table__wrapper thead th.sticky-repeat),
.modern-table :deep(.v-table__wrapper thead th.sticky-status) {
  z-index: 5 !important;
}

.modern-table :deep(.v-table__wrapper::-webkit-scrollbar) {
  width: 6px;
}

.modern-table :deep(.v-table__wrapper::-webkit-scrollbar-track) {
  background: rgba(0, 0, 0, 0.05);
}

.modern-table :deep(.v-table__wrapper::-webkit-scrollbar-thumb) {
  background: rgba(25, 118, 210, 0.3);
  border-radius: 3px;
}

.modern-table :deep(.v-table__wrapper::-webkit-scrollbar-thumb:hover) {
  background: rgba(25, 118, 210, 0.5);
}


/* ══════════════════════════════════════════════════════════════════════════════
   Add/Edit Proactive dialog — palette and components ported 1:1 from
   add-edit-proactive-modal-v20.html so the built screen matches the approved
   mockup: same #4A2E80 primary, same card-style radio rows, same section order.
   Prefixed .mk- ("mockup") so none of it can collide with the page's older
   .form-section / .search-field rules that still style the filter drawer.
   ══════════════════════════════════════════════════════════════════════════════ */
/* 🔴 The mockup inherits border-box from Tailwind's preflight; these plain inputs would
   otherwise add their 1.25rem horizontal padding ON TOP of width:100% and overflow every
   card they sit in. Scoped to the dialog so no other screen's layout shifts. */
.add-proactive-dialog .mk-input,
.add-proactive-dialog .mk-select,
.add-proactive-dialog .mk-check-tile,
.add-proactive-dialog .mk-radio-row,
.add-proactive-dialog .mk-seg-row,
.add-proactive-dialog .mk-card-gray,
.add-proactive-dialog .mk-card-outline,
.add-proactive-dialog .mk-card-white,
.add-proactive-dialog .mk-question,
.add-proactive-dialog .mk-quick,
.add-proactive-dialog .mk-choice {
  box-sizing: border-box;
}

/* Keep a radio row's label on one line where the mockup does; wrap only when genuinely tight. */
.add-proactive-dialog .mk-radio-row > span,
.add-proactive-dialog .mk-seg-row > span {
  line-height: 1.3;
}

.add-proactive-dialog {
  --mk-primary: #4a2e80;
  --mk-primary-dark: #3b2467;
  --mk-card-gray: #f4f6f9;
  --mk-border: #e2e8f0;
  --mk-ink-muted: #8592a6;
}

.add-proactive-dialog .dialog-content { background: #fff; }

/* ── labels ── */
.mk-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.875rem;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 0.5rem;
}
.mk-label-sm { margin-bottom: 0.375rem; }
.mk-label-xs { font-size: 0.75rem; margin-bottom: 0.375rem; }
.mk-label-xxs { font-size: 0.625rem; font-weight: 600; color: #6b7280; margin-bottom: 0.25rem; }
.mk-mb0 { margin-bottom: 0; }
.mk-mb { margin-bottom: 0.75rem; }
.mk-mt { margin-top: 0.75rem; }
.mk-pt { padding-top: 0.25rem; }
.mk-flag { width: 18px; height: 13px; object-fit: contain; }
.mk-flag-xs { width: 14px; height: 10px; object-fit: contain; }

.mk-block { margin-bottom: 1.25rem; }
.mk-card-title { font-size: 0.875rem; font-weight: 700; color: #1f2937; margin: 0 0 0.75rem; }
.mk-heading { font-size: 0.9375rem; font-weight: 700; color: #111827; margin: 0 0 0.75rem; }
.mk-eyebrow {
  font-size: 0.75rem; font-weight: 700; color: #6b7280;
  text-transform: uppercase; letter-spacing: 0.03em; margin: 0 0 0.625rem;
}
.mk-hint { font-size: 0.75rem; color: var(--mk-ink-muted); margin: 0.25rem 0 0; }

/* ── containers ── */
.mk-card-gray {
  background: var(--mk-card-gray);
  border-radius: 1rem;
  padding: 1.25rem;
  margin-bottom: 1.5rem;
}
.mk-card-outline {
  border: 1px solid var(--mk-border);
  border-radius: 1rem;
  padding: 1.25rem;
  margin-bottom: 1.5rem;
  background: #fff;
}
.mk-card-white {
  background: #fff;
  border: 1px solid var(--mk-border);
  border-radius: 1rem;
  padding: 1.25rem;
}
.mk-divider-dashed {
  border-top: 1px dashed #d1d5db;
  margin: 1.25rem 0;
}
.mk-grid-2 {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;
  margin-bottom: 1.25rem;
}
@media (max-width: 640px) { .mk-grid-2 { grid-template-columns: 1fr; } }

/* section that greys out when its header checkbox is off */
.mk-sub { transition: opacity 0.2s ease; }
.mk-sub.is-off { opacity: 0.6; pointer-events: none; }

/* ── header checkbox ── */
.mk-check-head {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  cursor: pointer;
  user-select: none;
  margin-bottom: 0.75rem;
  font-size: 0.875rem;
  font-weight: 700;
  color: #1f2937;
}
.mk-check-head-sm { font-size: 0.75rem; margin-bottom: 0; }
.mk-checkbox {
  width: 1rem; height: 1rem; flex-shrink: 0;
  accent-color: var(--mk-primary);
  cursor: pointer;
}

/* ── card-style radio rows (Action Type, Display Period, Tax Planning) ── */
.mk-radio-row {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.875rem 1.125rem;
  border: 1px solid var(--mk-border);
  border-radius: 0.875rem;
  background: #fff;
  cursor: pointer;
  transition: all 0.15s ease;
  font-size: 0.875rem;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 0.75rem;
}
.mk-radio-row:hover { border-color: #c9bede; }
.mk-radio-row.is-checked { border-color: var(--mk-primary); background: rgba(74, 46, 128, 0.045); }
.mk-radio-row.is-disabled { opacity: 0.55; cursor: not-allowed; background: #f1f5f9; }
.mk-radio-row input[type='radio'] {
  margin-top: 0.125rem;
  accent-color: var(--mk-primary);
  width: 1rem; height: 1rem; flex-shrink: 0;
}
.mk-row-2, .mk-row-3 { display: flex; flex-wrap: wrap; gap: 0.75rem; }
.mk-row-2 > .mk-radio-row, .mk-row-3 > .mk-radio-row { flex: 1 1 140px; margin-bottom: 0; }

/* ── primary-segment radio cards ── */
.mk-row-4 {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.75rem;
}
@media (max-width: 640px) { .mk-row-4 { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
.mk-seg-row {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  border: 1px solid var(--mk-border);
  border-radius: 0.875rem;
  background: #fff;
  color: #4b5563;
  cursor: pointer;
  transition: all 0.15s ease;
  text-align: center;
  font-size: 0.875rem;
  font-weight: 600;
}
.mk-seg-row:hover { border-color: #c9bede; }
.mk-seg-row.is-checked { border-color: var(--mk-primary); background: rgba(74, 46, 128, 0.06); color: var(--mk-primary); }
.mk-seg-row input[type='radio'] { accent-color: var(--mk-primary); width: 1rem; height: 1rem; flex-shrink: 0; }

/* ── day pills + presets ── */
.mk-preset-row { display: flex; flex-wrap: wrap; gap: 0.5rem; margin-bottom: 0.75rem; }
.mk-day-row { display: flex; flex-wrap: wrap; gap: 0.625rem; }
.mk-preset {
  padding: 0.4rem 0.9rem;
  border-radius: 9999px;
  border: 1px solid var(--mk-border);
  background: #fff;
  color: #4b5563;
  font-size: 0.75rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.15s ease;
}
.mk-preset:hover { border-color: #c9bede; }
.mk-preset.is-active { background: var(--mk-primary); border-color: var(--mk-primary); color: #fff; }
.mk-preset:disabled { background: #f1f5f9; border-color: var(--mk-border); color: #94a3b8; opacity: 0.7; cursor: not-allowed; }
.mk-day-pill {
  width: 3.1rem; height: 2.5rem;
  display: flex; align-items: center; justify-content: center;
  border: 1px solid var(--mk-border);
  border-radius: 9999px;
  background: #fff;
  color: #4b5563;
  font-size: 0.8125rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.15s ease;
}
.mk-day-pill:hover { border-color: #c9bede; }
.mk-day-pill.is-checked { background: var(--mk-primary); border-color: var(--mk-primary); color: #fff; }
.mk-day-pill:disabled { background: #f1f5f9; border-color: var(--mk-border); color: #94a3b8; opacity: 0.7; cursor: not-allowed; }
.mk-day-pill-wide { width: auto; padding: 0 1rem; height: 2rem; }

/* ── birthday ── */
.mk-birthday {
  margin-top: 0.75rem;
  padding-left: 0.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
}
.mk-birthday-inline {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: #1f2937;
}

/* ── plain pill inputs / selects ── */
.mk-input {
  width: 100%;
  background: #fff;
  border: 1px solid var(--mk-border);
  border-radius: 9999px;
  padding: 0.625rem 1.25rem;
  font-size: 0.875rem;
  color: #1f2937;
  outline: none;
  transition: all 0.15s ease;
}
.mk-input::placeholder { color: #9ca3af; }
.mk-input:focus { border-color: var(--mk-primary); box-shadow: 0 0 0 4px rgba(74, 46, 128, 0.1); }
.mk-input:disabled { background: #f1f5f9; color: #94a3b8; opacity: 0.7; cursor: not-allowed; }
.mk-input-xs { padding: 0.5rem 1rem; font-size: 0.75rem; }
.mk-input-tiny { width: 5rem; padding: 0.375rem 0.75rem; text-align: center; }

.mk-select-wrap { position: relative; }
.mk-select {
  width: 100%;
  background: #fff;
  border: 1px solid var(--mk-border);
  border-radius: 9999px;
  padding: 0.625rem 2.5rem 0.625rem 1.25rem;
  font-size: 0.875rem;
  color: #1f2937;
  outline: none;
  appearance: none;
  -webkit-appearance: none;
  transition: all 0.15s ease;
  cursor: pointer;
}
.mk-select:focus { border-color: var(--mk-primary); box-shadow: 0 0 0 4px rgba(74, 46, 128, 0.1); }
.mk-select:disabled { background: #f1f5f9; color: #94a3b8; opacity: 0.7; cursor: not-allowed; }
.mk-caret {
  pointer-events: none;
  position: absolute;
  right: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: #9ca3af;
}

/* Vuetify fields, pulled onto the mockup's pill shape and palette */
.add-proactive-dialog .mk-field :deep(.v-field) {
  border-radius: 9999px !important;
  background: #fff;
}
.add-proactive-dialog .mk-field :deep(.v-field__outline__start),
.add-proactive-dialog .mk-field :deep(.v-field__outline__end),
.add-proactive-dialog .mk-field :deep(.v-field__outline__notch::before),
.add-proactive-dialog .mk-field :deep(.v-field__outline__notch::after) {
  border-color: var(--mk-border);
}
.add-proactive-dialog .mk-field :deep(.v-field--focused .v-field__outline__start),
.add-proactive-dialog .mk-field :deep(.v-field--focused .v-field__outline__end),
.add-proactive-dialog .mk-field :deep(.v-field--focused .v-field__outline__notch::before),
.add-proactive-dialog .mk-field :deep(.v-field--focused .v-field__outline__notch::after) {
  border-color: var(--mk-primary);
}
.add-proactive-dialog .mk-field :deep(.v-field__input) {
  font-size: 0.875rem;
  color: #1f2937;
  min-height: 44px;
  padding-inline: 1.25rem;
}
.add-proactive-dialog .mk-field :deep(.v-chip) {
  background: rgba(74, 46, 128, 0.08);
  color: var(--mk-primary);
  font-weight: 700;
}

/* ── continue questions ── */
.mk-questions { display: flex; flex-direction: column; gap: 0.75rem; }
.mk-question {
  background: var(--mk-card-gray);
  border-radius: 1rem;
  padding: 1.25rem;
}
.mk-question-head { display: flex; align-items: center; justify-content: space-between; }
.mk-question-head .mk-card-title { margin-bottom: 0; }

/* ── quick reply ── */
.mk-quick {
  background: #fff;
  border: 1px solid var(--mk-border);
  border-radius: 0.75rem;
  padding: 1rem;
  margin-top: 0.25rem;
}
.mk-quick-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.75rem;
}
.mk-quick-body { margin-top: 0.75rem; }
.mk-quick-empty {
  font-size: 0.6875rem;
  color: #9ca3af;
  font-style: italic;
  text-align: center;
  padding: 0.5rem 0;
  margin: 0;
}
.mk-choice {
  background: var(--mk-card-gray);
  border: 1px solid var(--mk-border);
  border-radius: 0.75rem;
  padding: 0.875rem;
  margin-top: 0.625rem;
}
.mk-choice-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #e5e7eb;
  padding-bottom: 0.5rem;
  margin-bottom: 0.625rem;
}
.mk-choice-label { font-size: 0.6875rem; font-weight: 700; color: #4b5563; }
.mk-choice-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.625rem;
}
@media (max-width: 640px) { .mk-choice-grid { grid-template-columns: 1fr; } }

/* ── checkbox tiles (tax year / state) ── */
.mk-year-grid, .mk-state-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.625rem;
}
@media (max-width: 640px) { .mk-year-grid, .mk-state-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
.mk-check-tile {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: #374151;
  background: #fff;
  border: 1px solid var(--mk-border);
  border-radius: 0.75rem;
  padding: 0.625rem 0.75rem;
  cursor: pointer;
  transition: all 0.15s ease;
}
.mk-check-tile:hover { border-color: #c9bede; }
.mk-check-tile.is-checked { background: rgba(74, 46, 128, 0.06); border-color: var(--mk-primary); color: var(--mk-primary); }
.mk-check-tile.is-removed { border-style: dashed; color: #b45309; }
.mk-nest { margin-top: 0.75rem; margin-bottom: 0.75rem; padding-left: 0.25rem; }

/* ── buttons ── */
.mk-btn-primary {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 1.25rem;
  border-radius: 9999px;
  background: var(--mk-primary);
  color: #fff;
  font-size: 0.875rem;
  font-weight: 700;
  cursor: pointer;
  border: none;
  transition: background-color 0.15s ease;
}
.mk-btn-primary:hover { background: var(--mk-primary-dark); }
.mk-btn-sm {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.5rem 1rem;
  border-radius: 9999px;
  background: var(--mk-primary);
  color: #fff;
  font-size: 0.6875rem;
  font-weight: 700;
  border: none;
  cursor: pointer;
}
.mk-btn-sm:disabled { opacity: 0.45; cursor: not-allowed; }
.mk-icon-btn {
  width: 1.75rem; height: 1.75rem;
  display: flex; align-items: center; justify-content: center;
  border-radius: 9999px;
  color: #9ca3af;
  background: transparent;
  border: none;
  cursor: pointer;
  transition: all 0.15s ease;
}
.mk-icon-btn:hover { background: #fff; color: #ef4444; }
.mk-cif-actions { display: flex; flex-wrap: wrap; gap: 0.75rem; align-items: center; }
.mk-btn-upload {
  display: inline-flex; align-items: center; gap: 0.5rem;
  padding: 0.625rem 1rem;
  border-radius: 9999px;
  border: 1px solid #a7f3d0;
  background: #ecfdf5;
  color: #047857;
  font-size: 0.75rem; font-weight: 700; cursor: pointer;
  transition: background-color 0.15s ease;
}
.mk-btn-upload:hover { background: #d1fae5; }
.mk-btn-download {
  display: inline-flex; align-items: center; gap: 0.5rem;
  padding: 0.625rem 1rem;
  border-radius: 9999px;
  background: var(--mk-primary);
  color: #fff;
  font-size: 0.75rem; font-weight: 700; cursor: pointer; border: none;
  transition: background-color 0.15s ease;
}
.mk-btn-download:hover { background: var(--mk-primary-dark); }

/* ── unresolved-reference warning (§17/§18) ── */
.removed-state-chip { cursor: help; }

.mk-action-row {
  display: flex;
  align-items: center;
  gap: 0.625rem;
}
.mk-action-select { flex: 1 1 auto; min-width: 0; }
.mk-add-action { flex: 0 0 auto; white-space: nowrap; }

.mk-warn {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.75rem;
  color: #b45309;
  margin-top: 0.5rem;
}

/* ── fade-in, straight from the mockup ── */
.mk-fade { animation: mkFadeIn 0.18s ease; }
@keyframes mkFadeIn {
  from { opacity: 0; transform: translateY(-4px); }
  to { opacity: 1; transform: translateY(0); }
}

/* ── footer: mockup purple on the primary action ── */
.add-proactive-dialog .btn-save {
  background: var(--mk-primary) !important;
  color: #fff !important;
  border-radius: 9999px;
  font-weight: 700;
}
.add-proactive-dialog .btn-save:hover { background: var(--mk-primary-dark) !important; }
.add-proactive-dialog .btn-close { border-radius: 9999px; font-weight: 700; }
/* ── Footer toggles: mockup palette ────────────────────────────────────────────
   The base .custom-switch rules above paint the active track #6366F1 (indigo),
   which is the one thing in the footer still off-palette. Overridden here rather
   than edited in place so the original block stays a single, untouched unit —
   and !important is required only because that block already uses it.

   Track  #D1D5DB off / #4A2E80 on, knob white with the mockup's soft shadow,
   label 12px bold #374151 — all straight from .toggle-track / .toggle-knob /
   "text-xs font-bold text-gray-700" in add-edit-proactive-modal-v20.html.

   🔴 Geometry (44x24 track, 20px knob) is deliberately LEFT ALONE. The mockup's
   40x20/16px is the same proportion to the eye, and Vuetify drives the knob's
   travel with its own internal transform that the existing rules already nudge
   into place — resizing the track without re-deriving that transform is how a
   knob ends up stopping short of the end of its track. */
.add-proactive-dialog .custom-switch :deep(.v-switch__track) {
  background-color: #d1d5db !important;
  border-color: #d1d5db;
  opacity: 1;
}
.add-proactive-dialog .custom-switch :deep(.v-selection-control--dirty .v-switch__track) {
  background-color: var(--mk-primary, #4a2e80) !important;
  border-color: var(--mk-primary, #4a2e80);
  opacity: 1;
}
.add-proactive-dialog .custom-switch :deep(.v-switch__thumb) {
  background-color: #fff !important;
  color: #fff;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
}
/* Vuetify tints the ripple from the `color` prop; keep it in-palette too. */
.add-proactive-dialog .custom-switch :deep(.v-selection-control__input::before) {
  background-color: var(--mk-primary, #4a2e80);
}
.add-proactive-dialog .custom-switch :deep(.v-selection-control--focus-visible .v-switch__track) {
  box-shadow: 0 0 0 4px rgba(74, 46, 128, 0.15);
}
.add-proactive-dialog .custom-switch :deep(.v-label) {
  font-size: 12px;
  font-weight: 700;
  color: #374151;
  opacity: 1;
}
/* A disabled control must read as LESS prominent than an enabled one. The base
   block paints its label pure black (#000), which made a switch the admin cannot
   touch the highest-contrast text in the footer. */
.add-proactive-dialog .custom-switch :deep(.v-selection-control--disabled .v-label) {
  color: #9ca3af !important;
}
.add-proactive-dialog .custom-switch :deep(.v-selection-control--disabled .v-switch__track) {
  opacity: 0.55;
}
</style>
